import axios, { AxiosError } from 'axios';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import * as tus from 'tus-js-client';
import confetti from 'canvas-confetti';
import Flag from 'react-world-flags';
import { useApp } from '../context/AppContext';
import { useEvent } from '../SSE';
import './ServerPanel.css';
import './plugins-modal.css';
import './ModalMapsCs.css';
import './ModalWorlds.css';
import serverFeatures from '../../server-features.json';
import { BadgeCheck, CircleX, Earth, Folder, Package, Power, PowerOff, Puzzle, Settings, SquareChevronRight, Trash2, Zap, Map as MapIcon, Clipboard, Save, Users, Plus, X, Tag, Search, Star, ArrowRight, ArrowLeft, Cpu, Activity, Terminal, LayoutDashboard, RotateCw, Loader2, Info, Gamepad2, Check, Eye, EyeOff, Share2, Globe } from 'lucide-react';

const APP_NAME = import.meta.env.VITE_APP_NAME || 'Voltary';

const LOCATION_FLAGS: Record<string, string> = {
  nbg1: 'DE',
  fsn1: 'DE',
  ash: 'US',
  hil: 'US',
  hel1: 'FI',
};

function LocationFlag({ location }: { location?: string }) {
  const code = location ? LOCATION_FLAGS[location.toLowerCase()] : null;
  if (!code) return <Globe size={14} />;
  return (
    <Flag
      code={code}
      style={{ width: '1em', height: '1em', verticalAlign: '-0.125em', borderRadius: '2px' }}
    />
  );
}
import { ToastNotification } from '../components/ToastNotification';
import { StatsPanel } from '../components/StatsPanel';
import { LogoutButton } from '../components/LogoutButton';
import { MaintenanceOverlay } from '../components/MaintenanceOverlay';
import {
  PlanModal,
  AddonsModal,
  PlayerListModal,
  WorldsModal,
  ChangeVersionModal,
  ChangeTypeModal,
  PropertiesModal,
  SessionInfoModal,
  MapSelectionModal,
  PermissionsModal,
  DeleteConfirmModal,
  ImportantAlertModal,
  StatsModal,
  ShutdownProgressModal,
  StartingProgressModal,
  type AddonKind,
  type PlayerListKind,
} from '../components/server/ServerModals';

interface Server {
  id: string;
  name: string;
  type: string;
  version: string;
  status: 'online' | 'offline' | 'maintenance';
  players: number;
  maxPlayers: number;
  active?: boolean;
  seconds_active?: number;
  ip?: string | null;
  plan_id?: string;
  plan?: Plan;
  game?: string;
  permissions?: string;
  ftp_password?: string;
  subdomain?: string | null;
  /** True when the server has been provisioned with a VPS at least once.
   *  Replaces the old `!!server.ip` gate used for console/files/plugins. */
  provisioned?: boolean;
}

interface ConfigFile {
  file: string;
  name: string;
  description: string;
  type: string;
  content: string;
}

interface ConfigData {
  serverId: string;
  serverName: string;
  gameType: string;
  version: string;
  serverActive?: boolean;
  source?: 'server' | 'r2';
  configs: ConfigFile[];
}

interface Plan {
  id: string;
  label: string;
  description: string;
  price: number;
  location?: string;
}

interface DownloadedPlugins {
  filename: string;
  url: string;
}

export function ServerPanel() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state, updateServer, removeServer, fetchVersions, invalidateCache } = useApp();
  // Maintenance: cuando está activa, bloqueamos power-on / restart /
  // start-container desde el cliente. El backend igual rechazaría con
  // 503, pero cortamos antes para no abrir modales de "iniciando" que
  // nunca van a cerrarse. shutdown/stop siguen permitidos (apagar no
  // tiene que bloquearse nunca).
  const isMaintenanceOn = state.maintenance !== null;

  // Cross-tab sync: react to state changes from another tab of the same
  // user. When this server starts/stops/restarts or its config (plan,
// version, type) changes elsewhere, we invalidate the cached servers
  // payload and refetch so IP, plan, active_by and timestamps stay
  // current.
  // Cross-tab sync: react to state changes from another tab of the same
  // user. Patches the local + context state directly from the event
  // payload (no refetch), so the change is visible immediately even if
  // a refetch would have been blocked by a stale closure or a slow
  // network. The same pattern is used by the Dashboard.
  useEvent('server.state_changed', (e) => {
    if (!server || String(e.server_id) !== String(server.id)) return;
    const active = e.state === 'starting' || e.state === 'running';
    // IP is intentionally NOT in the payload anymore — clients address the
    // server by subdomain. Only `active`/`status` change here.
    setServer(prev => (prev ? { ...prev, active, status: active ? 'online' : 'offline' } : prev));
    updateServer(e.server_id, {
      active,
      status: active ? 'online' : 'offline',
    });
    invalidateCache('servers');

    // Close the shutdown progress modal when the SSE confirms the
    // server has reached the 'stopped' state. This is what makes the
    // modal feel "integrated with SSE": it stays open until the
    // backend says the shutdown is actually done.
    if (e.state === 'stopped') {
      closeShutdownModal();
    }

    // El usuario acaba de iniciar un restart → cualquier cambio de
    // software/version pendiente se va a aplicar, así que limpiamos el
    // banner.
    if (e.state === 'restarting') {
      setPendingSoftwareChange(null);
    }
    // Note: the starting progress modal is closed by the resource-
    // monitoring polling effect below, not by the SSE state event —
    // because SSE `state: running` can fire before the container is
    // actually serving stats, which is a more reliable liveness check.
  });

  // For server.updated we still need to refetch because the event does
  // not carry the new plan/version/type values, only the list of changed
  // fields. The refetch runs in the background without showing any
  // loading state.
  const refetchCurrentServer = (serverId: string) => {
    if (!server || String(serverId) !== String(server.id)) return;
    invalidateCache('servers');
    void fetchServer();
  };
  useEvent('server.updated', (e) => refetchCurrentServer(e.server_id));

  // If the server this tab is viewing is deleted from another tab (or
  // even this same tab via the modal), drop the cached payload and
  // bounce the user back to the dashboard. Without this, the user is
  // stuck on a page that no longer matches a real server.
  useEvent('server.deleted', (e) => {
    if (!server || String(e.server_id) !== String(server.id)) return;
    invalidateCache('servers');
    removeServer(e.server_id);
    navigate('/dashboard');
  });
  useEvent('cash.updated', (e) => {
    // Best-effort: stash the new cash in the global context so any
    // header that reads from state.cash stays in sync without a full
    // reload. Use setState via the existing context updater.
    if (typeof e.new_cash === 'number') {
      // updateBalance is exposed via useApp at the Dashboard; in this
      // component we only patch the local server shape if needed.
    }
  });

  const [server, setServer] = useState<Server | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Almacenamos el timestamp de inicio (en ms) cuando el servidor está activo
  const [serverStartTime, setServerStartTime] = useState<number | null>(null);
  // Tiempo actual mostrado (calculado)
  const [activeTime, setActiveTime] = useState(0);
  const [showModal, setShowModal] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [powering, setPowering] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [configData, setConfigData] = useState<ConfigData | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [selectedConfigFile, setSelectedConfigFile] = useState<ConfigFile | null>(null);
  const [editedContent, setEditedContent] = useState('');
  // Plan selection state
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  // Server plan info (now included in server data from list endpoint)
  const [serverPlanInfo, setServerPlanInfo] = useState<{ game: string; type: string; plan: Plan } | null>(null);

  // Plugin management state
  const [plugins, setPlugins] = useState<DownloadedPlugins[]>([]);
  const [loadingPlugins, setLoadingPlugins] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchingPlugins, setSearchingPlugins] = useState(false);
  const [downloadingPlugin, setDownloadingPlugin] = useState<string | null>(null);
  const [deletingPlugin, setDeletingPlugin] = useState<string | null>(null);
  // Popular plugins state
  const [popularPlugins, setPopularPlugins] = useState<any[]>([]);
  const [loadingPopularPlugins, setLoadingPopularPlugins] = useState(false);

  // Mod management state
  const [mods, setMods] = useState<DownloadedPlugins[]>([]);
  const [loadingMods, setLoadingMods] = useState(false);
  const [searchModQuery, setSearchModQuery] = useState('');
  const [searchModResults, setSearchModResults] = useState<any[]>([]);
  const [searchingMods, setSearchingMods] = useState(false);
  const [downloadingMod, setDownloadingMod] = useState<string | null>(null);
  const [deletingMod, setDeletingMod] = useState<string | null>(null);

  // Popular mods state
  const [popularMods, setPopularMods] = useState<any[]>([]);
  const [loadingPopularMods, setLoadingPopularMods] = useState(false);

  // Mobile tab state for plugins/mods modal
  const [mobilePluginsTab, setMobilePluginsTab] = useState<'installed' | 'search'>('installed');

  // Mobile tab state for files modal
  const [mobileFilesTab, setMobileFilesTab] = useState<'files' | 'editor'>('files');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Pagination state for plugins search
  const [pluginsPage, setPluginsPage] = useState(0);
  const [hasMorePlugins, setHasMorePlugins] = useState(true);
  const [loadingMorePlugins, setLoadingMorePlugins] = useState(false);

  // Pagination state for mods search
  const [modsPage, setModsPage] = useState(0);
  const [hasMoreMods, setHasMoreMods] = useState(true);
  const [loadingMoreMods, setLoadingMoreMods] = useState(false);

  // Map upload state
  const [hasPendingMap, setHasPendingMap] = useState(false);
  const [uploadingMap, setUploadingMap] = useState(false);
  const [mapFile, setMapFile] = useState<File | null>(null);
  const [hasUploadedMap, setHasUploadedMap] = useState(false);
  const [downloadingMap, setDownloadingMap] = useState(false);
  const [deletingWorld, setDeletingWorld] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Whitelist management state
  const [whitelist, setWhitelist] = useState<string[]>([]);
  const [loadingWhitelist, setLoadingWhitelist] = useState(false);
  const [whitelistEnabled, setWhitelistEnabled] = useState(false);
  const [whitelistToggling, setWhitelistToggling] = useState(false);
  const [addingToWhitelist, setAddingToWhitelist] = useState(false);
  const [removingFromWhitelist, setRemovingFromWhitelist] = useState<string | null>(null);

  // Blacklist (ban) management state
  const [blacklist, setBlacklist] = useState<string[]>([]);
  const [loadingBlacklist, setLoadingBlacklist] = useState(false);
  const [addingToBlacklist, setAddingToBlacklist] = useState(false);
  const [removingFromBlacklist, setRemovingFromBlacklist] = useState<string | null>(null);

  // Server starting modal state (show when server is active but < 2 minutes)
  const [showStartingModal, setShowStartingModal] = useState(false);

  // Session info state (shown after session stops)
  const [showSessionInfo, setShowSessionInfo] = useState(false);
  const [sessionSeconds, setSessionSeconds] = useState(0);
  const [sessionCost, setSessionCost] = useState(0);

  // Session state - the new model charges per active session, not per server uptime.
  // The server can be off while the session is still active (e.g. user clicked
  // "Apagar Servidor" to free resources without ending the billing session).
  const [sessionActive, setSessionActive] = useState(false);

  // Loading state for the new "Apagar Servidor" (shutdown only) action.
  const [shuttingDown, setShuttingDown] = useState(false);

  // Después de apagar el servidor, esperamos ~15s (a que docker stop termine)
  // y entonces el botón de Apagar Servidor se transforma en "Encender Servidor".
  const [encenderAvailable, setEncenderAvailable] = useState(false);
  const [shutdownCountdown, setShutdownCountdown] = useState<number | null>(null);

  // Ref al interval del countdown para poder limpiarlo si el usuario
  // toca "Encender Servidor" antes de que se cumplan los 15s.
  const shutdownCountdownRef = useRef<number | null>(null);

  // Ref al loading del start-container para que el botón muestre "Encendiendo..."
  const [startingContainer, setStartingContainer] = useState(false);

  // Timestamp (ms) del último "stopping_docker" registrado en la DB.
  // Si está set y server.active === true, significa que el contenedor
  // está apagado pero la sesión sigue activa (Apagar Servidor previo) →
  // hay que mostrar "Encender Servidor" en lugar de "Apagar Servidor".
  const [lastStopDockerTime, setLastStopDockerTime] = useState<number | null>(null);

  // Timestamp (ms) del último "starting_docker" (Encender Servidor).
  // Se compara contra lastStopDockerTime para saber cuál fue la
  // acción más reciente del usuario sobre el contenedor.
  const [lastStartDockerTime, setLastStartDockerTime] = useState<number | null>(null);

  // Track the last fetched server ID to force refresh when navigating to a newly created server
  const [lastFetchedId, setLastFetchedId] = useState<string | null>(null);

  // Saving world modal state (shown when server is stopping)
  const [showSavingModal, setShowSavingModal] = useState(false);

  // Restarting modal state (shown when server is restarting)
  const [showRestartingModal, setShowRestartingModal] = useState(false);

  // Shutdown progress modal: opens when the user initiates a shutdown,
  // stays open until the SSE event `server.state_changed: stopped` arrives.
  // If the SSE event doesn't arrive within 3 minutes (network issue, server
  // didn't shut down cleanly), a safety timeout force-closes the modal so
  // the user isn't permanently stuck.
  const [shutdownModalOpen, setShutdownModalOpen] = useState(false);
  const shutdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openShutdownModal = () => {
    setShutdownModalOpen(true);
    if (shutdownTimeoutRef.current !== null) clearTimeout(shutdownTimeoutRef.current);
    shutdownTimeoutRef.current = setTimeout(() => {
      setShutdownModalOpen(false);
      setShowErrorToast(true);
      setErrorToastMessage('El servidor no confirmó el apagado. Recargá la página.');
    }, 3 * 60 * 1000);
  };
  const closeShutdownModal = () => {
    setShutdownModalOpen(false);
    if (shutdownTimeoutRef.current !== null) {
      clearTimeout(shutdownTimeoutRef.current);
      shutdownTimeoutRef.current = null;
    }
  };

  // Starting progress modal: opens when the user initiates a server start
  // (handleTogglePower on-path or handleStartContainer). Closes when the
  // /api/servers/:id/stats polling endpoint confirms the container is up
  // (returns 200). Polls every 60s; if no 200 within 4 minutes, force-closes
  // with an error toast. Coexists with the "Servidor iniciando…" banner.
  const [startingModalOpen, setStartingModalOpen] = useState(false);
  const openStartingModal = () => setStartingModalOpen(true);
  const closeStartingModal = () => setStartingModalOpen(false);

  // Server name edit state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorToastMessage, setErrorToastMessage] = useState('');
  const [showImportantAlert, setShowImportantAlert] = useState(false);
  const [importantAlertMessage, setImportantAlertMessage] = useState('');

  // Map change state for Counter Strike
  const [showMapModal, setShowMapModal] = useState(false);
  const [changingMap, setChangingMap] = useState(false);
  const [currentMap, setCurrentMap] = useState<string>('');

  // Permissions management state
  const [permissionsList, setPermissionsList] = useState<any[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [discordIdInput, setDiscordIdInput] = useState('');
  const [addingPermission, setAddingPermission] = useState(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);

  // Subdomain prompt state — auto-opens when an owner opens a server that
  // doesn't yet have a subdomain. El modal es obligatorio: el usuario no
  // puede cerrarlo sin asignar uno. El flag `subdomainDismissed` ya no
  // existe — solo el guardado exitoso lo cierra.
  const [showSubdomainPrompt, setShowSubdomainPrompt] = useState(false);
  const [subdomainInput, setSubdomainInput] = useState('');
  const [subdomainCheck, setSubdomainCheck] = useState<{ valid: boolean; available: boolean } | null>(null);
  const [subdomainChecking, setSubdomainChecking] = useState(false);
  const [savingSubdomain, setSavingSubdomain] = useState(false);
  const [subdomainError, setSubdomainError] = useState<string | null>(null);
  // Ref al input para poder enfocarlo cuando el usuario clickea en
  // cualquier parte del preview (el "boarding pass") — el campo de
  // texto se vuelve implícitamente todo el card.
  const subdomainInputRef = useRef<HTMLInputElement>(null);

  const focusSubdomainInput = useCallback(() => {
    const el = subdomainInputRef.current;
    if (!el) return;
    // Si el input ya está activo, no movemos el foco: el usuario podría
    // estar editando en una posición específica del texto y no queremos
    // tirar su selección a la nada.
    if (document.activeElement === el) return;
    el.focus();
  }, []);

  // Change version state (Minecraft only)
  const [changingVersion, setChangingVersion] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<string>('');
  const [versionSearchQuery, setVersionSearchQuery] = useState<string>('');

  // Change software/type state (Minecraft only)
  const [changingType, setChangingType] = useState(false);
  const [selectedType, setSelectedType] = useState<string>('');
  // Versión pendiente de auto-aplicar cuando se cierra el modal de versión
  // sin guardar tras un cambio de software. null = no hay flujo activo.
  const [pendingLatestVersion, setPendingLatestVersion] = useState<string | null>(null);
  // Cambio de software/version pendiente de aplicar en el contenedor.
  // Se setea cuando change-type/change-versions responde OK mientras el
  // server está activo (la DB se actualiza pero el contenedor sigue
  // corriendo con el software viejo). Se limpia cuando el usuario
  // reinicia (SSE server.state_changed state='restarting').
  // { type?, version? } — solo se setea el campo que cambió.
  const [pendingSoftwareChange, setPendingSoftwareChange] = useState<
    { type?: string; version?: string } | null
  >(null);

  // Classic CS 1.6 maps
  const classicCSMaps = [
    { name: 'de_dust2', image: '/assets/maps/cs1.6/de_dust2.webp', description: 'Desert' },
    { name: 'de_dust', image: '/assets/maps/cs1.6/de_dust.webp', description: 'Dust' },
    { name: 'de_aztec', image: '/assets/maps/cs1.6/de_aztec.webp', description: 'Aztec' },
    { name: 'de_inferno', image: '/assets/maps/cs1.6/de_inferno.webp', description: 'Inferno' },
    { name: 'de_nuke', image: '/assets/maps/cs1.6/de_nuke.webp', description: 'Nuke' },
    { name: 'de_prodigy', image: '/assets/maps/cs1.6/de_prodigy.webp', description: 'Prodigy' },
    { name: 'de_train', image: '/assets/maps/cs1.6/de_train.webp', description: 'Train' },
    { name: 'de_vertigo', image: '/assets/maps/cs1.6/de_vertigo.webp', description: 'Vertigo' },
    { name: 'cs_assault', image: '/assets/maps/cs1.6/cs_assault.webp', description: 'Assault' },
    { name: 'cs_havana', image: '/assets/maps/cs1.6/cs_havana.webp', description: 'Havana' },
    { name: 'cs_italy', image: '/assets/maps/cs1.6/cs_italy.webp', description: 'Italy' },
    { name: 'cs_office', image: '/assets/maps/cs1.6/cs_office.webp', description: 'Office' },
    { name: 'cs_estate', image: '/assets/maps/cs1.6/cs_estate.webp', description: 'Estate' },
    { name: 'cs_militia', image: '/assets/maps/cs1.6/cs_militia.webp', description: 'Militia' },
  ];

  // Counter Strike Source maps (using available images)
  const cssMaps = [
    { name: 'de_dust2', image: '/assets/maps/css/de_dust2.webp', description: 'Desert' },
    { name: 'de_dust', image: '/assets/maps/css/de_dust.webp', description: 'Dust' },
    { name: 'de_aztec', image: '/assets/maps/css/de_aztec.webp', description: 'Aztec' },
    { name: 'de_inferno', image: '/assets/maps/css/de_inferno.webp', description: 'Inferno' },
    { name: 'de_nuke', image: '/assets/maps/css/de_nuke.webp', description: 'Nuke' },
    { name: 'de_prodigy', image: '/assets/maps/css/de_prodigy.webp', description: 'Prodigy' },
    { name: 'de_train', image: '/assets/maps/css/de_train.webp', description: 'Train' },
    { name: 'de_vertigo', image: '/assets/maps/css/de_vertigo.webp', description: 'Vertigo' },
    { name: 'cs_assault', image: '/assets/maps/css/cs_assault.webp', description: 'Assault' },
    { name: 'cs_havana', image: '/assets/maps/css/cs_havana.webp', description: 'Havana' },
    { name: 'cs_italy', image: '/assets/maps/css/cs_italy.webp', description: 'Italy' },
    { name: 'cs_office', image: '/assets/maps/css/cs_office.webp', description: 'Office' },
    { name: 'cs_estate', image: '/assets/maps/css/cs_estate.webp', description: 'Estate' },
    { name: 'cs_militia', image: '/assets/maps/css/cs_militia.webp', description: 'Militia' },
  ];

  // Determine which maps to show based on server type
  const isCSS = server?.type === 'Source';
  const currentMaps = isCSS ? cssMaps : classicCSMaps;

  // Effect to warn user before leaving page while server is saving or restarting
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (showSavingModal || showRestartingModal) {
        e.preventDefault();
        const message = showSavingModal
          ? 'Guardando datos y cerrando el servidor... Si sales ahora, podrías causar errores visuales o pérdida de progreso. ¿Quieres salir de todas formas?'
          : 'Reiniciando el servidor... Si sales ahora, podrías causar errores visuales o pérdida de progreso. ¿Quieres salir de todas formas?';
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [showSavingModal, showRestartingModal]);

  // Preload map images on page load
  useEffect(() => {
    classicCSMaps.forEach(map => {
      const img = new Image();
      img.src = map.image;
    });
    cssMaps.forEach(map => {
      const img = new Image();
      img.src = map.image;
    });
  }, []);

  // On mount, show cached data immediately for snappy UX, then always
  // revalidate in the background. This handles both:
  //  - the user navigates back to a server they already visited
  //  - the user deep-links or refreshes a server they haven't loaded yet
  //  - the user is returning to a server whose state was changed in
  //    another tab while this panel was unmounted
  //
  // Cache TTL is 10s (see CACHE_DURATION), so within that window the
  // background revalidate is a no-op. After that window we always
  // hit the API to keep the panel honest.
  useEffect(() => {
    if (!id) return;

    const cachedServer = state.servers.find(s => String(s.id) === String(id));

    if (cachedServer) {
      setServer(cachedServer);

      // Set plan info from cache (or null if absent)
      setServerPlanInfo({
        game: cachedServer.game || cachedServer.type,
        type: cachedServer.type,
        plan: cachedServer.plan ?? null,
      } as typeof serverPlanInfo extends infer T ? T : never);

      if (cachedServer.active) {
        updateServerStartTimeFromBackend(cachedServer.id);
        setSessionActive(true);
      } else {
        setSessionActive(false);
      }
      setLoading(false);
    }

    setLastFetchedId(id);

    // Always revalidate in the background to pick up changes from other
    // tabs that happened while this panel was unmounted.
    invalidateCache('servers');
    void fetchServer();
  }, [id, state.servers]);

  // Asegura que `state.versions` esté cargado para los modales de cambio
  // de versión/software. Si el usuario llega directo a /server/:id (refresh,
  // deep-link) el cache de AppContext puede estar vacío y la lista de
  // versiones del dropdown saldría vacía.
  useEffect(() => {
    if (!state.versions) {
      fetchVersions().catch((err) => {
        console.error('Error fetching versions:', err);
      });
    }
  }, [state.versions, fetchVersions]);

  // Efecto para actualizar el tiempo activo basado en serverStartTime
  useEffect(() => {
    let interval: number | undefined;
    if (server?.active && serverStartTime !== null) {
      // Función para recalcular el tiempo transcurrido
      const updateTime = () => {
        const now = Date.now();
        const elapsed = Math.floor((now - serverStartTime) / 1000);
        setActiveTime(elapsed > 0 ? elapsed : 0);
      };
      // Actualizar inmediatamente
      updateTime();
      // Luego cada segundo (o con mayor frecuencia si quieres más precisión)
      interval = window.setInterval(updateTime, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [server?.active, serverStartTime]);

  // Track the last id that the "starting" modal was shown for. Without
  // this, a passive refetch (e.g. triggered by an SSE event from another
  // tab) can reset activeTime to 0, re-firing the effect and re-opening
  // the modal even when the server has been running for a while.
  const [lastStartingModalFor, setLastStartingModalFor] = useState<string | null>(null);

  // Efecto para mostrar el modal de "servidor iniciando" cuando el server
  // acaba de arrancar (activeTime < 120s). Trackeamos qué server.id ya
  // disparó el modal en esta sesión para que un type change u otro cambio
  // de configuración no vuelva a abrirlo.
  useEffect(() => {
    // Reset tracker si el server ya está apagado
    if (!server?.active) {
      setLastStartingModalFor(null);
      return;
    }
    if (server?.active && activeTime > 0 && activeTime < 120) {
      if (lastStartingModalFor !== String(server.id)) {
        setShowStartingModal(true);
        setLastStartingModalFor(String(server.id));
      }
    } else if (activeTime >= 120) {
      setShowStartingModal(false);
    }
  }, [server?.active, activeTime, server?.id, lastStartingModalFor]);

  // Auto-open the subdomain prompt when an owner lands on a server that
  // doesn't have one yet. El modal es obligatorio: el único camino de
  // cerrarlo es guardando un subdominio válido. Re-disparar si cambia el
  // `id` (navegación a otro server) o si `subdomain` pasa a null desde
  // otro tab; un set durante el mismo ciclo de vida no reabre el modal.
  // Counter Strike no usa subdominio: `getServerAddress` devuelve la IP
  // del VPS (los clientes conectan con `connect <ip>:27015`, no por DNS),
  // así que no tiene sentido pedirle uno al owner.
  useEffect(() => {
    if (
      server &&
      server.subdomain == null &&
      server.permissions === 'owner'
    ) {
      setShowSubdomainPrompt(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server?.id, server?.subdomain, server?.permissions]);

  // Live availability check while the user types. Debounced 350ms so we
  // don't hammer the endpoint on every keystroke. Re-runs only on
  // input changes — opening/closing the modal without typing is a no-op.
  useEffect(() => {
    const candidate = subdomainInput.trim().toLowerCase();
    if (!candidate) {
      setSubdomainCheck(null);
      setSubdomainError(null);
      setSubdomainChecking(false);
      return;
    }

    setSubdomainChecking(true);
    const handle = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/api/subdomains/check`,
          { params: { subdomain: candidate }, withCredentials: true }
        );
        const { valid, available } = res.data ?? {};
        setSubdomainCheck({ valid: !!valid, available: !!available });
        if (!valid) {
          setSubdomainError('Formato inválido (3-17 caracteres, minúsculas, dígitos y guiones, sin empezar/terminar en guion)');
        } else if (!available) {
          setSubdomainError('Este subdominio ya está en uso');
        } else {
          setSubdomainError(null);
        }
      } catch (err) {
        setSubdomainError('No se pudo verificar disponibilidad');
        setSubdomainCheck(null);
      } finally {
        setSubdomainChecking(false);
      }
    }, 350);

    return () => clearTimeout(handle);
  }, [subdomainInput]);

  const handleSaveSubdomain = async () => {
    if (!id) return;
    const candidate = subdomainInput.trim().toLowerCase();
    if (!candidate) return;
    // Detectamos el modo ANTES de cualquier setState: el toast se
    // ajusta a "asignado" (no había subdominio previo) o "actualizado"
    // (estamos reemplazando uno existente).
    const wasEditing = server?.subdomain != null;
    // Don't submit if the live check says it's invalid or already taken.
    // (If the check hasn't completed yet — subdomainCheck === null with
    // no error — we let it through; the backend will re-validate and
    // return 409 if there's a race.)
    if (subdomainCheck && (!subdomainCheck.valid || !subdomainCheck.available)) {
      return;
    }

    setSavingSubdomain(true);
    setSubdomainError(null);
    try {
      const res = await axios.put(
        `${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/subdomain`,
        { subdomain: candidate },
        { withCredentials: true }
      );

      // Update local state + context cache so every other tab/component
      // sees the change without a refetch.
      const finalSubdomain = res.data?.subdomain ?? candidate;
      setServer(prev => (prev ? { ...prev, subdomain: finalSubdomain } : prev));
      updateServer(id, { subdomain: finalSubdomain });

      setShowSubdomainPrompt(false);
      setSubdomainInput('');
      setSubdomainCheck(null);
      setToastMessage(
        wasEditing
          ? `Subdominio actualizado: ${finalSubdomain}.node.voltary.org`
          : `Subdominio asignado: ${finalSubdomain}.node.voltary.org`
      );
      setShowToast(true);
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Error al guardar el subdominio';
      setSubdomainError(msg);
    } finally {
      setSavingSubdomain(false);
    }
  };

  // Abre el modal en modo edición: pre-carga el subdominio actual en
  // el input, resetea el estado de validación, y muestra el modal.
  // Solo el owner puede cambiar el subdominio (es lo mismo que el
  // auto-open inicial — ambos paths respetan permissions === 'owner').
  const handleOpenChangeSubdomain = useCallback(() => {
    if (!server || server.permissions !== 'owner' || !server.subdomain) return;
    setSubdomainInput(server.subdomain);
    setSubdomainCheck(null);
    setSubdomainError(null);
    setShowSubdomainPrompt(true);
  }, [server]);

  // Cancela el modal de edición: descarta lo que el usuario haya tipeado
  // y vuelve a abrir el server con el subdominio intacto. Solo tiene
  // sentido en modo edición (en modo asignar el modal es bloqueante y
  // no se puede cancelar). Restauramos el input al valor original del
  // server, no a '' — así no queda basura en el state si el modal se
  // vuelve a abrir después.
  const handleCancelSubdomain = useCallback(() => {
    if (server?.subdomain) {
      setSubdomainInput(server.subdomain);
    } else {
      setSubdomainInput('');
    }
    setSubdomainCheck(null);
    setSubdomainError(null);
    setShowSubdomainPrompt(false);
  }, [server]);

  const fetchPermissions = useCallback(async () => {
    if (!id || server?.permissions !== 'owner') return;
    setLoadingPermissions(true);
    try {
      const res = await axios.get(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/permissions`, { withCredentials: true });
      setPermissionsList(res.data.permissions || []);
    } catch (err) {
      console.error('Error fetching permissions:', err);
    } finally {
      setLoadingPermissions(false);
    }
  }, [id, server?.permissions]);

  const handleAddPermission = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !discordIdInput.trim()) return;
    setAddingPermission(true);
    setPermissionError(null);
    try {
      await axios.post(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/permissions`,
        { discord_id: discordIdInput.trim() },
        { withCredentials: true }
      );
      setDiscordIdInput('');
      fetchPermissions();
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Error al añadir colaborador';
      setPermissionError(errorMsg);
      if (err.response?.status === 404) {
        alert(errorMsg);
      }
    } finally {
      setAddingPermission(false);
    }
  };

  const handleRemovePermission = async (permissionId: string) => {
    if (!id) return;
    try {
      await axios.delete(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/permissions/${permissionId}`, { withCredentials: true });
      fetchPermissions();
    } catch (err) {
      console.error('Error removing permission:', err);
      alert('Error al eliminar colaborador');
    }
  };

  // Fetch specific server directly instead of all servers
  const fetchServer = async () => {
    try {
      // Try to fetch specific server directly if endpoint exists
      // For now, we still fetch all but optimize in context
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers`, { credentials: 'include' });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Error al cargar servidor');
        return;
      }

      const foundServer = data.servers?.find((s: Server) => String(s.id) === String(id));
      if (!foundServer) {
        setError('Servidor no encontrado');
        return;
      }

      // Si el servidor está activo, obtener el tiempo de inicio desde el backend
      if (foundServer.active) {
        await updateServerStartTimeFromBackend(foundServer.id);
        setSessionActive(true);
      } else {
        setServerStartTime(null);
        setActiveTime(0);
        setSessionActive(false);
      }

      setServer(foundServer);

      // Always sync serverPlanInfo from the fresh server payload, even
      // when the API returns plan: null (e.g. the plan_id points to a
      // non-existent plan, or the game has no plans configured). Without
      // this, an SSE-triggered refetch could leave stale plan info
      // visible if foundServer.plan happened to be null.
      setServerPlanInfo({
        game: foundServer.game || foundServer.type,
        type: foundServer.type,
        plan: foundServer.plan ?? null,
      } as typeof serverPlanInfo extends infer T ? T : never);
    } catch (err) {
      console.log(err)
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  // Función auxiliar para obtener serverStartTime desde /control
  const updateServerStartTimeFromBackend = async (serverId: string) => {
    try {
      const controlRes = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${serverId}/control`, {
        credentials: 'include',
      });
      const controlData = await controlRes.json();

      if (controlRes.ok && controlData.currentTime) {
        const currentTimeStr = controlData.currentTime.includes('Z') ? controlData.currentTime : controlData.currentTime + 'Z';
        const nowTime = new Date(currentTimeStr).getTime();

        // serverStartTime: momento del último "starting" (sesión o contenedor)
        if (controlData.serverStartTime) {
          const startTimeStr = controlData.serverStartTime.includes('Z') ? controlData.serverStartTime : controlData.serverStartTime + 'Z';
          const startTime = new Date(startTimeStr).getTime();
          if (!isNaN(startTime) && !isNaN(nowTime) && startTime <= nowTime) {
            setServerStartTime(startTime);
          } else {
            console.warn('Fechas inválidas recibidas del backend');
            setServerStartTime(Date.now());
          }
        } else {
          setServerStartTime(null);
        }

        // lastStopDockerTime: momento del último "stopping_docker" (Apagar
        // Servidor previo). Si existe y es válido, lo guardamos para
        // decidir el botón en el render.
        if (controlData.lastStopDockerTime) {
          const stopTimeStr = controlData.lastStopDockerTime.includes('Z')
            ? controlData.lastStopDockerTime
            : controlData.lastStopDockerTime + 'Z';
          const stopTime = new Date(stopTimeStr).getTime();
          if (!isNaN(stopTime)) {
            setLastStopDockerTime(stopTime);
          }
        } else {
          setLastStopDockerTime(null);
        }

        // lastStartDockerTime: momento del último "starting_docker"
        // (Encender Servidor). Lo comparamos con lastStopDockerTime para
        // deducir qué botón mostrar.
        if (controlData.lastStartDockerTime) {
          const startTimeStr = controlData.lastStartDockerTime.includes('Z')
            ? controlData.lastStartDockerTime
            : controlData.lastStartDockerTime + 'Z';
          const startTime = new Date(startTimeStr).getTime();
          if (!isNaN(startTime)) {
            setLastStartDockerTime(startTime);
          }
        } else {
          setLastStartDockerTime(null);
        }
      } else {
        console.warn('No se pudo obtener control del backend');
        setServerStartTime(Date.now());
      }
    } catch (error) {
      console.error('Error de red al obtener tiempo de control:', error);
      setServerStartTime(Date.now());
    }
  };

  const formatTime = (seconds: number): string => {
    const safeSeconds = Math.max(0, seconds);
    const hrs = Math.floor(safeSeconds / 3600);
    const mins = Math.floor((safeSeconds % 3600) / 60);
    const secs = safeSeconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const fireConfetti = () => {
    const duration = 2.5 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    const randomInRange = (min: number, max: number) =>
      Math.random() * (max - min) + min;

    const interval = window.setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      });
    }, 250);
  };

  const notificationAudioRef = useRef<HTMLAudioElement | null>(null);

  const primeNotificationAudio = () => {
    if (notificationAudioRef.current) {
      try {
        notificationAudioRef.current.currentTime = 0;
      } catch {
        // ignore
      }
      return;
    }
    try {
      const audio = new Audio('/sounds/notifications.mp3');
      audio.preload = 'auto';
      audio.volume = 0.7;
      notificationAudioRef.current = audio;
      const p = audio.play();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          audio.pause();
          audio.currentTime = 0;
        }).catch(() => {
          // ignore
        });
      }
    } catch (err) {
      console.error('Error al primear el audio de notificación:', err);
    }
  };

  const playNotificationSound = () => {
    try {
      const audio =
        notificationAudioRef.current ??
        (() => {
          const a = new Audio('/sounds/notifications.mp3');
          a.preload = 'auto';
          a.volume = 0.7;
          notificationAudioRef.current = a;
          return a;
        })();

      audio.currentTime = 0;
      const p = audio.play();
      if (p && typeof p.then === 'function') {
        p.catch((err) => {
          console.error('Error reproduciendo sonido de notificación:', err);
        });
      }
    } catch (err) {
      console.error('Error reproduciendo sonido de notificación:', err);
    }
  };

  const notifyServerReady = () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;

    const show = () => {
      try {
        new Notification('Servidor listo', {
          body: 'Tu servidor ya está en línea y aceptando jugadores.',
          icon: '/favicon.ico',
          tag: 'server-ready',
        });
      } catch (err) {
        console.error('Error mostrando notificación del sistema:', err);
      }
    };

    if (Notification.permission === 'granted') {
      show();
    } else if (Notification.permission === 'default') {
      void Notification.requestPermission().then((perm) => {
        if (perm === 'granted') show();
      });
    }
  };

  /**
   * Dirección pública del servidor. El formato depende del juego:
   *
   *  - Counter Strike: IP real del VPS (los clientes conectan vía
   *    launcher con `connect <ip>:27015`).
   *  - Minecraft / Terraria (y cualquier otro): subdomain público
   *    (`*.node.voltary.org`). El subdomain persiste aunque el
   *    contenedor esté apagado.
   *
   * Devuelve `null` si no hay dirección asignable. Los callers deben
   * manejar el null con un "No asignada" / deshabilitar el botón.
   */
  const getServerAddress = (s: Server | null): string | null => {
    if (!s) return null;
    if (s.subdomain && s.subdomain.length > 0) return `${s.subdomain}.node.voltary.org`;
    return null;
  };

  const handleTogglePower = useCallback(async (planId?: string) => {
    if (powering) {
      alert('Ya estás cambiando el estado del servidor. Esperá a que termine.');
      return;
    }
    if (!server) return;

    const isTurningOn = !server.active;
    const actionText = isTurningOn ? 'encender' : 'apagar';

    // Maintenance gate: bloquear el encendido. El apagado se permite
    // incluso en mantenimiento (queremos que el user pueda cortar la
    // sesión si lo necesita). El backend además rechaza con 503 si el
    // admin decide mantener la regla dura.
    if (isTurningOn && isMaintenanceOn) {
      alert(`La plataforma está en mantenimiento. ${state.maintenance!.reason}`);
      return;
    }

    setPowering(true);

    primeNotificationAudio();

    // Optimistic UI update - update immediately
    const optimisticActive = isTurningOn;
    const optimisticStatus = isTurningOn ? 'online' : 'offline';
    setServer(prev => prev ? { ...prev, active: optimisticActive, status: optimisticStatus } : null);

    // Also update in context cache
    if (id) {
      updateServer(id, { active: optimisticActive, status: optimisticStatus });
    }

    // Show saving modal when turning off the server
    if (!isTurningOn) {
      setShowSavingModal(true);
      // Open the blocking shutdown progress modal; it will close when the
      // SSE event `server.state_changed: stopped` arrives (or via the
      // 3-minute safety timeout).
      openShutdownModal();
    } else {
      // Open the blocking starting progress modal immediately on click
      // so the user gets feedback before any network round-trip. Closes
      // when SSE delivers `server.state_changed: running`.
      openStartingModal();
    }

    // Sync the session state with the server state on power toggle:
    // turning on starts a session; turning off ends it.
    setSessionActive(isTurningOn);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10 * 60 * 1000);

      // Build request body - use planId if provided, otherwise use existing plan from serverPlanInfo
      const planIdToUse = planId || (isTurningOn ? serverPlanInfo?.plan?.id : undefined);
      const body = isTurningOn && planIdToUse ? JSON.stringify({ plan_id: planIdToUse }) : undefined;

      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/power`, {
        method: 'POST',
        credentials: 'include',
        signal: controller.signal,
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body,
      });

      clearTimeout(timeoutId);

      if (!res.ok) {
        const data = await res.json();
        const errorMsg = data.error || `Error al ${actionText} el servidor`;
        // Check if this is the specific Hetzner resource_unavailable error when starting server
        if (isTurningOn && errorMsg.includes('resource_unavailable')) {
          setImportantAlertMessage('⚠️  Todos los servidores en este plan estan ocupados');
          setShowImportantAlert(true);
        } else {
          alert(errorMsg);
        }
        // Revert optimistic update on error
        setServer(prev => prev ? { ...prev, active: !optimisticActive, status: !optimisticActive ? 'online' : 'offline' } : null);
        if (id) {
          updateServer(id, { active: !optimisticActive, status: !optimisticActive ? 'online' : 'offline' });
        }
        // Ocultar modal de inicio si hay error al encender
        if (isTurningOn) {
          setShowStartingModal(false);
          closeStartingModal();
        }
        // Si falló un apagado, cerrar el modal de progreso para no
        // dejar al usuario atrapado
        if (!isTurningOn) {
          closeShutdownModal();
          setShowSavingModal(false);
        }
        // Hide saving modal if there's an error turning off
        if (!isTurningOn) {
          setShowSavingModal(false);
        }
        setPowering(false);
        return;
      }

      const data = await res.json();

      // Si el backend devolvió un warning no bloqueante (ej. Forge/Fabric
      // en Plan Basico), lo mostramos como alert. El server YA está
      // iniciando — el warning es informativo, no detiene la operación.
      if (data.warning) {
        alert(data.warning);
      }

      // Update with actual server response. The backend no longer returns
      // `ip` in this payload (clients address via subdomain only).
      const newActiveState = data.active;
      setServer(prev => prev ? { ...prev, active: newActiveState, status: newActiveState ? 'online' : 'offline' } : null);

      if (id) {
        updateServer(id, {
          active: newActiveState,
          status: newActiveState ? 'online' : 'offline',
          serverStartTime: data.serverStartTime
        });
      }

      if (newActiveState) {
        if (data.serverStartTime && data.currentTime) {
          const startTime = new Date(data.serverStartTime).getTime();
          const nowTime = new Date(data.currentTime).getTime();
          if (!isNaN(startTime) && !isNaN(nowTime) && startTime <= nowTime) {
            setServerStartTime(startTime);
          }
        }
        // Recargar archivos de configuración del servidor
        if (showModal === 'properties') {
          loadConfigFiles();
        }
      } else {
        // Get session info from API response
        const secondsRunning = data.secondsRunning || 0;
        const cost = data.cost || 0;
        setSessionSeconds(secondsRunning);
        setSessionCost(cost);
        setServerStartTime(null);
        setActiveTime(0);
        // Show session info when server turns off
        if (secondsRunning > 0) {
          setShowSessionInfo(true);
          setShowSavingModal(false);
        }
        // Refetch only when turning off to get accurate data
        await fetchServer();
        // Recargar archivos de configuración desde R2
        if (showModal === 'properties') {
          loadConfigFiles();
        }
      }
    } catch (err) {
      console.error('Error en power toggle:', err);
      window.location.reload();
      // Revert optimistic update on error
      setServer(prev => prev ? { ...prev, active: !optimisticActive, status: !optimisticActive ? 'online' : 'offline' } : null);
      if (id) {
        updateServer(id, { active: !optimisticActive, status: !optimisticActive ? 'online' : 'offline' });
      }
      // Ocultar modal de inicio si hay error de conexión al encender
      if (isTurningOn) {
        setShowStartingModal(false);
        closeStartingModal();
      }
      // Hide saving modal if there's a connection error turning off
      if (!isTurningOn) {
        setShowSavingModal(false);
        closeShutdownModal();
      }
    } finally {
      setTimeout(() => setPowering(false), 1500);
    }
  }, [id, server, powering, updateServer, showModal, isMaintenanceOn]);

  // Apagar Servidor: solo apaga el contenedor/juego. La sesión de facturación
  // permanece activa, por lo que el cronómetro y el cargo siguen corriendo
  // hasta que el usuario pulse "Detener Sesión".
  const handleShutdownServer = useCallback(async () => {
    if (!id || !server || !server.active) return;
    if (shuttingDown) {
      alert('Ya estás apagando el servidor. Esperá a que termine.');
      return;
    }
    if (powering) {
      alert('Hay otra operación en curso. Esperá a que termine antes de apagar.');
      return;
    }

    const confirmShutdown = window.confirm(
      '¿Apagar el servidor?\n\nEl servidor se detendrá pero la sesión seguirá activa y se seguirá facturando hasta que detengas la sesión.'
    );
    if (!confirmShutdown) return;

    setShuttingDown(true);
    setShowSavingModal(true);
    openShutdownModal();

    try {
      // Endpoint dedicado para apagar sin cerrar la sesión. Si el backend aún
      // no lo expone, debe aceptar este comportamiento (mismo efecto que
      // /power con un flag "end_session=false" o equivalente).
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/shutdown`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        alert(data?.error || 'Error al apagar el servidor');
        setShowSavingModal(false);
        closeShutdownModal();
        return;
      }

      // Servidor apagado, pero la sesión sigue activa.
      setServer(prev => (prev ? { ...prev, active: false, status: 'offline' } : prev));
      if (id) {
        updateServer(id, { active: false, status: 'offline' });
      }
      setServerStartTime(null);
      setActiveTime(0);
      setShowSavingModal(false);
      // sessionActive NO se modifica: la facturación continúa.

      // Empezamos un countdown de 15s. Pasado ese tiempo, el botón de
      // "Apagar Servidor" se transforma en "Encender Servidor".
      const PRENDER_DELAY_SECONDS = 15;
      setShutdownCountdown(PRENDER_DELAY_SECONDS);
      setEncenderAvailable(false);

      // Si quedó un interval anterior colgado, lo limpiamos.
      if (shutdownCountdownRef.current !== null) {
        window.clearInterval(shutdownCountdownRef.current);
      }

      let remaining = PRENDER_DELAY_SECONDS;
      const tick = window.setInterval(() => {
        remaining -= 1;
        if (remaining <= 0) {
          window.clearInterval(tick);
          shutdownCountdownRef.current = null;
          setShutdownCountdown(null);
          setEncenderAvailable(true);
        } else {
          setShutdownCountdown(remaining);
        }
      }, 1000);
      shutdownCountdownRef.current = tick;
    } catch (err) {
      console.error('Error en shutdown:', err);
      window.location.reload();
      setShowSavingModal(false);
    } finally {
      setShuttingDown(false);
    }
  }, [id, server, shuttingDown, powering, updateServer]);

  // Encender Servidor: vuelve a levantar el contenedor del juego en la misma
  // VPS (la sesión sigue activa, así que no se crea una VPS nueva ni se
  // vuelve a cobrar el cargo inicial). Es la inversa de handleShutdownServer.
  const handleStartContainer = useCallback(async () => {
    if (!id || !server) return;
    if (startingContainer) {
      alert('Ya estás encendiendo el contenedor. Esperá a que termine.');
      return;
    }
    if (shuttingDown) {
      alert('Hay otra operación en curso. Esperá a que termine antes de encender.');
      return;
    }
    if (isMaintenanceOn) {
      alert(`La plataforma está en mantenimiento. ${state.maintenance!.reason}`);
      return;
    }

    setStartingContainer(true);
    // Open the blocking starting progress modal immediately; closes when
    // SSE delivers `server.state_changed: running`.
    openStartingModal();

    primeNotificationAudio();

    // Limpiamos el countdown (el usuario tocó Encender antes de los 15s
    // o justo cuando se cumplieron; en cualquier caso ya no lo necesitamos).
    if (shutdownCountdownRef.current !== null) {
      window.clearInterval(shutdownCountdownRef.current);
      shutdownCountdownRef.current = null;
    }

    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/start-container`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        alert(data?.error || 'Error al encender el servidor');
        closeStartingModal();
        return;
      }

      // Servidor encendido: la UI vuelve a "encendido".
      setServer(prev => (prev ? { ...prev, active: true, status: 'online' } : prev));
      if (id) {
        updateServer(id, { active: true, status: 'online' });
      }
      setEncenderAvailable(false);
      setShutdownCountdown(null);
      setLastStopDockerTime(null);
    } catch (err) {
      console.error('Error en start-container:', err);
      closeStartingModal();
      window.location.reload();
    } finally {
      setStartingContainer(false);
    }
  }, [id, server, startingContainer, shuttingDown, updateServer, isMaintenanceOn]);

  // Cleanup del interval si el componente se desmonta.
  useEffect(() => {
    return () => {
      if (shutdownCountdownRef.current !== null) {
        window.clearInterval(shutdownCountdownRef.current);
        shutdownCountdownRef.current = null;
      }
      if (shutdownTimeoutRef.current !== null) {
        clearTimeout(shutdownTimeoutRef.current);
        shutdownTimeoutRef.current = null;
      }
    };
  }, []);

  // Polling del monitoring de recursos para cerrar el modal de "encendiendo".
  // Mientras el modal esté abierto, pegamos contra /api/servers/:id/stats
  // cada 60s. Si responde 200 → el contenedor está vivo y reportando
  // recursos → cerramos el modal. Si pasan 4 minutos sin un 200 → cerramos
  // con toast de error (el contenedor nunca reportó estar vivo).
  useEffect(() => {
    if (!startingModalOpen || !id) return;

    const POLL_INTERVAL_MS = 60 * 1000;
    const TIMEOUT_MS = 4 * 60 * 1000;
    const startedAt = Date.now();
    let cancelled = false;
    let intervalId: number | null = null;

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await fetch(
          `${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/stats`,
          { credentials: 'include' }
        );
        if (cancelled) return;
        if (res.ok) {
          // El servidor ya está reportando recursos → cerramos el modal.
          setStartingModalOpen(false);
          fireConfetti();
          playNotificationSound();
          if (document.hidden) {
            notifyServerReady();
          }
          return;
        }
      } catch {
        // Error de red: lo dejamos pasar y seguimos intentando.
      }
      if (cancelled) return;
      if (Date.now() - startedAt >= TIMEOUT_MS) {
        setStartingModalOpen(false);
        setShowErrorToast(true);
        setErrorToastMessage('El servidor no reportó recursos en 4 minutos. Recargá la página.');
      }
    };

    // Primer poll inmediato (puede que el server ya esté respondiendo).
    void poll();
    intervalId = window.setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalId !== null) window.clearInterval(intervalId);
    };
  }, [startingModalOpen, id]);

  // Si al cargar la página (o refrescar) hay un lastStopDockerTime, significa
  // que el usuario había tocado "Apagar Servidor" antes. Levantamos el
  // estado del botón en consecuencia:
  //   - Si pasaron < 15s: arrancamos el countdown para mostrar
  //     "Apagando... (Xs)" y al final "Encender Servidor".
  //   - Si pasaron >= 15s: directamente marcamos "Encender Servidor".
  //
  // Solo aplicamos esto si server.active está en true (lo que en este modelo
  // significa "sesión activa") y no estamos en medio de un shutdown/start
  // local (eso lo maneja handleShutdownServer / handleStartContainer).
  useEffect(() => {
    if (
      lastStopDockerTime === null ||
      !server?.active ||
      shuttingDown ||
      startingContainer
    ) {
      return;
    }

    // Si la última acción del usuario fue un Encender Servidor más
    // reciente que el Apagar previo, no hay nada que esperar: el
    // contenedor está corriendo. (El caso típico: el usuario tocó
    // Apagar, esperó 15s, tocó Encender, y refrescó la página.)
    if (lastStartDockerTime !== null && lastStartDockerTime > lastStopDockerTime) {
      setShutdownCountdown(null);
      setEncenderAvailable(false);
      return;
    }

    const PRENDER_DELAY_SECONDS = 15;
    const elapsed = (Date.now() - lastStopDockerTime) / 1000;

    // Limpiamos cualquier interval previo antes de iniciar uno nuevo.
    if (shutdownCountdownRef.current !== null) {
      window.clearInterval(shutdownCountdownRef.current);
    }

    if (elapsed >= PRENDER_DELAY_SECONDS) {
      setShutdownCountdown(null);
      setEncenderAvailable(true);
      return;
    }

    const remaining = Math.max(1, Math.ceil(PRENDER_DELAY_SECONDS - elapsed));
    setShutdownCountdown(remaining);
    setEncenderAvailable(false);

    let counter = remaining;
    const tick = window.setInterval(() => {
      counter -= 1;
      if (counter <= 0) {
        window.clearInterval(tick);
        shutdownCountdownRef.current = null;
        setShutdownCountdown(null);
        setEncenderAvailable(true);
      } else {
        setShutdownCountdown(counter);
      }
    }, 1000);
    shutdownCountdownRef.current = tick;
  }, [lastStopDockerTime, lastStartDockerTime, server?.active, shuttingDown, startingContainer]);

  // Detener Sesión: cierra la sesión de facturación. Si el servidor está
  // encendido, también lo apaga. Siempre muestra el resumen de sesión y
  // desactiva sessionActive (deja de facturar).
  const handleStopSession = useCallback(async () => {
    if (!id || !server) return;
    if (powering) {
      alert('Hay un cambio de estado en curso. Esperá a que termine antes de detener la sesión.');
      return;
    }
    if (shuttingDown) {
      alert('Ya estás apagando el servidor. Esperá a que termine antes de detener la sesión.');
      return;
    }
    if (!sessionActive && !server.active) return;

    setPowering(true);

    try {
      if (server.active) {
        // Open the blocking shutdown progress modal IMMEDIATELY so the
        // user gets feedback the moment they click. The modal stays open
        // until the SSE event `server.state_changed: stopped` arrives
        // (or the 3-minute safety timeout fires).
        openShutdownModal();

        const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/power`, {
          method: 'POST',
          credentials: 'include',
        });

        if (!res.ok) {
          const data = await res.json().catch(() => null);
          alert(data?.error || 'Error al detener la sesión');
          closeShutdownModal();
          setPowering(false);
          return;
        }

        const data = await res.json();
        const secondsRunning = data.secondsRunning || 0;
        const cost = data.cost || 0;
        setSessionSeconds(secondsRunning);
        setSessionCost(cost);
        setServerStartTime(null);
        setActiveTime(0);
        setServer(prev => (prev ? { ...prev, active: false, status: 'offline' } : prev));
        if (id) {
          updateServer(id, { active: false, status: 'offline' });
        }
        if (secondsRunning > 0) {
          setShowSessionInfo(true);
        }
        if (showModal === 'properties') {
          loadConfigFiles();
        }
      } else {
        // Servidor apagado (p. ej. el usuario usó "Apagar Servidor" antes).
        // Solo cerramos la sesión y mostramos el resumen acumulado.
        try {
          const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/session`, {
            method: 'DELETE',
            credentials: 'include',
          });
          if (res.ok) {
            const data = await res.json();
            setSessionSeconds(data.secondsRunning || 0);
            setSessionCost(data.cost || 0);
            if ((data.secondsRunning || 0) > 0) {
              setShowSessionInfo(true);
            }
          }
        } catch {
          // Si el endpoint no existe aún, simplemente limpiamos estado local.
        }
      }

      // En cualquier caso, la sesión queda inactiva.
      setSessionActive(false);
      setLastStopDockerTime(null);
      setEncenderAvailable(false);
      setShutdownCountdown(null);
      if (shutdownCountdownRef.current !== null) {
        window.clearInterval(shutdownCountdownRef.current);
        shutdownCountdownRef.current = null;
      }
    } catch (err) {
      console.error('Error deteniendo sesión:', err);
      window.location.reload();
    } finally {
      setTimeout(() => setPowering(false), 1500);
    }
  }, [id, server, sessionActive, powering, shuttingDown, updateServer, showModal]);

  // Handle server restart
  const handleRestart = useCallback(async () => {
    if (!server || !server.active) return;
    if (restarting) {
      alert('Ya estás reiniciando el servidor. Esperá a que termine.');
      return;
    }
    if (isMaintenanceOn) {
      alert(`La plataforma está en mantenimiento. ${state.maintenance!.reason}`);
      return;
    }

    const confirmRestart = window.confirm('¿Estás seguro de que quieres reiniciar el servidor? Esto detendrá el servidor, eliminará archivos y lo reiniciará.');
    if (!confirmRestart) return;

    setRestarting(true);
    setShowRestartingModal(true);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10 * 60 * 1000);

      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/restart`, {
        method: 'POST',
        credentials: 'include',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!res.ok) {
        const data = await res.json();
        alert(data.error || 'Error al reiniciar el servidor');
        setRestarting(false);
        setShowRestartingModal(false);
        return;
      }

      const data = await res.json();
      setShowRestartingModal(false);

      // Recargar archivos de configuración
      if (showModal === 'properties') {
        loadConfigFiles();
      }
    } catch (err) {
      console.error('Error en restart:', err);
      window.location.reload();
      setShowRestartingModal(false);
    } finally {
      setTimeout(() => setRestarting(false), 1500);
    }
  }, [id, server, restarting, showModal, isMaintenanceOn]);

  // Function to fetch plans and show modal
  const handleStartWithPlan = useCallback(async () => {
    if (!server || server.active) return;

    // Map server type to game
    const gameMap: Record<string, string> = {
      'java': 'Minecraft',
      'bedrock': 'Minecraft',
      '1.6': 'Counter Strike',
      'Source': 'Counter Strike',
      'terraria-vanilla': 'Terraria',
    };

    const game = server.game;

    setLoadingPlans(true);
    setShowPlanModal(true);

    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/plans/${game}`, {
        credentials: 'include',
      });
      const data = await res.json();

      if (res.ok && data.plans) {
        setPlans(data.plans);
      } else {
        alert(data.error || 'Error al cargar planes');
        setShowPlanModal(false);
      }
    } catch (err) {
      console.error('Error loading plans:', err);
      window.location.reload();
      setShowPlanModal(false);
    } finally {
      setLoadingPlans(false);
    }
  }, [server]);

  // Handle plan selection - save plan to DB (without starting server)
  const handlePlanSelect = useCallback(async (plan: Plan) => {
    if (!server || !id) return;

    setSelectedPlan(plan);
    setShowPlanModal(false);

    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/plan`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: plan.id }),
        credentials: 'include',
      });

      const data = await res.json();

      if (res.ok) {
        // Update local state with new plan
        setServer(prev => prev ? { ...prev, plan_id: plan.id, plan } : null);
        setServerPlanInfo(prev => prev ? { ...prev, plan } : { game: server.type, type: server.type, plan });
      } else {
        alert(data.error || 'Error al guardar el plan');
      }
    } catch (err) {
      console.error('Error saving plan:', err);
      window.location.reload();
    }
  }, [server, id]);

  // Connect to logs stream
  // Handle delete

  const handleDelete = useCallback(async () => {
    if (!server) return;
    setDeleting(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (res.ok) {
        // Remove from context cache
        if (id) {
          removeServer(id);
        }
        navigate('/dashboard');
      } else {
        const data = await res.json();
        alert(data.error || 'Error al eliminar servidor');
      }
    } catch {
      window.location.reload();
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }, [id, server, navigate, removeServer]);

  // Change map for Counter Strike server
  const handleChangeMap = useCallback(async (mapName: string) => {
    if (!id || !server || !server.active || changingMap) return;

    const confirmChange = window.confirm(`¿Estás seguro de que quieres cambiar al mapa "${mapName}"? El servidor se reiniciará con el nuevo mapa.`);
    if (!confirmChange) return;

    setChangingMap(true);

    // Use different endpoint based on server type
    const isCounterStrike = server.type === '1.6' || server.type === 'Source';
    const endpoint = isCounterStrike ? 'counterstrike-map' : 'map';

    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/${endpoint}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ map: mapName }),
      });
      const data = await res.json();

      if (res.ok) {
        setCurrentMap(mapName);
        setShowMapModal(false);
      } else {
        alert(data.error || 'Error al cambiar el mapa');
      }
    } catch (err) {
      console.error('Error changing map:', err);
      window.location.reload();
    } finally {
      setChangingMap(false);
    }
  }, [id, server, changingMap]);

  // Fetch current map when opening modal
  // Fetch current map
  const fetchCurrentMap = useCallback(async () => {
    if (!id) return;

    // Use different endpoint based on server type
    const isCounterStrike = server?.type === '1.6' || server?.type === 'Source';
    const endpoint = isCounterStrike ? 'counterstrike-map' : 'map';

    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/${endpoint}`, {
        credentials: 'include',
      });
      const data = await res.json();

      if (res.ok && data.currentMap) {
        setCurrentMap(data.currentMap);
      }
    } catch (err) {
      console.error('Error fetching current map:', err);
    }
  }, [id, server?.type]);

  // Open map modal and fetch current map
  const handleMapsClick = useCallback(() => {
    fetchCurrentMap();
    setShowMapModal(true);
  }, [fetchCurrentMap]);

  // Handle saving server name
  const handleSaveName = useCallback(async () => {
    if (!server || !id || !editedName.trim() || editedName.trim() === server.name) {
      setIsEditingName(false);
      return;
    }

    setSavingName(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editedName.trim() }),
        credentials: 'include',
      });

      const data = await res.json();
      console.log(data);

      if (res.ok) {
        // Update local state
        setServer(prev => prev ? { ...prev, name: editedName.trim() } : null);
        // Also update in context cache
        updateServer(id, { name: editedName.trim() });
        setIsEditingName(false);
      } else {
        alert(data.error || 'Error al guardar el nombre');
      }
    } catch {
      window.location.reload();
    } finally {
      setSavingName(false);
    }
  }, [server, id, editedName, updateServer]);

  // Start editing name
  const startEditingName = useCallback(() => {
    if (server) {
      setEditedName(server.name);
      setIsEditingName(true);
    }
  }, [server]);

  // Load config files - only get list without content
  const loadConfigFiles = async () => {
    if (!id) return;

    setLoadingConfig(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/config/${id}`, {
        credentials: 'include',
      });
      const data = await res.json();

      if (res.ok) {
        setConfigData(data);
        if (data.configs && data.configs.length > 0) {
          // Select first file and load its content
          setSelectedConfigFile(data.configs[0]);
          setEditedContent('Cargando...');
          // Load content of first file automatically
          loadFileContent(data.configs[0].file);
        }
      } else {
        alert(data.error || 'Error al cargar configuración');
      }
    } catch {
      window.location.reload();
    } finally {
      setLoadingConfig(false);
    }
  };

  // Reload config files when modal is opened or server state changes
  useEffect(() => {
    if (showModal === 'properties' && id) {
      loadConfigFiles();
    }
    // Check pending map when worlds modal is opened
    if (showModal === 'worlds' && id) {
      checkPendingMap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, id, server?.active]);

  // Load content of a specific file on demand
  const loadFileContent = async (fileName: string) => {
    if (!id) return;

    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/config/${id}/file/${encodeURIComponent(fileName)}`, {
        credentials: 'include',
      });
      const data = await res.json();

      if (res.ok && data.content !== undefined) {
        setEditedContent(data.content);
        // Update the selected file's content in configData
        if (configData) {
          setConfigData({
            ...configData,
            configs: configData.configs.map(c =>
              c.file === fileName ? { ...c, content: data.content } : c
            )
          });
        }
      } else {
        console.error('Error loading file:', data.error);
      }
    } catch (error) {
      console.error('Error loading file content:', error);
    }
  };

  // Guardar configuración
  const saveConfigFile = async () => {
    if (!id || !selectedConfigFile) return;

    setSavingConfig(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/config/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          file: selectedConfigFile.file,
          content: editedContent,
        }),
      });
      const data = await res.json();

      if (res.ok) {
        alert('✅ Configuración guardada exitosamente');
        // Re-fetch the file from API to get fresh content
        loadFileContent(selectedConfigFile.file);
      } else {
        alert(data.error || 'Error al guardar configuración');
      }
    } catch {
      window.location.reload();
    } finally {
      setSavingConfig(false);
    }
  };

  // Check if there's a pending map
  const checkPendingMap = async () => {
    if (!id) return;

    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/map`, {
        credentials: 'include',
      });
      const data = await res.json();

      if (res.ok) {
        setHasPendingMap(data.hasPendingMap || false);
        setHasUploadedMap(data.hasPendingMap || false);
      }
    } catch (error) {
      console.error('Error checking pending map:', error);
    }
  };

  // Upload map - using FormData with progress tracking
  const uploadMap = async (): Promise<void> => {
    if (!id || !mapFile) return;

    // Validar extensión (igual que antes)
    const validExtensions = ['zip', 'tar', 'gz'];
    const fileName = mapFile.name.toLowerCase();
    const fileExt = fileName.split('.').pop() || '';
    const isTarGz = fileName.endsWith('.tar.gz');
    if (!validExtensions.includes(fileExt) || (fileExt === 'gz' && !isTarGz)) {
      alert('⚠️ Extensión no compatible. Solo se permiten archivos .zip, .tar.gz, .tar o .gz');
      return;
    }

    setUploadingMap(true);
    setUploadProgress(0);

    try {
      // 0. Precheck de compatibilidad ANTES de subir nada.
      //    Extrae el level.dat del archivo, parsea la versión y la compara
      //    con la del servidor. Si el mapa es más nuevo que el server,
      //    abortamos con un alert claro y no subimos el archivo.
      const precheckForm = new FormData();
      precheckForm.append('map', mapFile);
      const precheckRes = await fetch(
        `${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/map/precheck`,
        {
          method: 'POST',
          credentials: 'include',
          body: precheckForm,
        }
      );
      const precheckData = await precheckRes.json().catch(() => null);

      if (!precheckRes.ok || !precheckData) {
        setUploadingMap(false);
        setUploadProgress(0);
        alert(precheckData?.error || 'No se pudo verificar el mapa antes de subirlo.');
        return;
      }

      if (!precheckData.compatible) {
        setUploadingMap(false);
        setUploadProgress(0);
        alert(
          `⚠️ Mapa incompatible\n\n${precheckData.message || `El mapa es de la versión ${precheckData.mapVersion} y tu servidor es ${precheckData.serverVersion}.`}\n\nPara usar este mapa, actualiza el servidor a una versión igual o superior.`
        );
        return;
      }

      // 1. Obtener URL de subida y metadatos desde Bun
      const authRes = await axios.post(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/map`, {}, {
        withCredentials: true,
      });
      const { uploadUrl, metadata } = authRes.data;

      // 2. Crear subida TUS
      const upload = new tus.Upload(mapFile, {
        endpoint: uploadUrl,
        retryDelays: [0, 3000, 5000, 10000, 20000],
        metadata: {
          filename: mapFile.name,
          filetype: mapFile.type,
          ...metadata, // serverId, userId, serverIp
        },
        chunkSize: 50 * 1024 * 1024, // 50 MB
        onProgress: (bytesUploaded, bytesTotal) => {
          const percent = (bytesUploaded / bytesTotal * 100).toFixed(2);
          setUploadProgress(parseFloat(percent));
        },
        onSuccess: () => {
          setUploadingMap(false);
          alert('✅ Mapa subido exitosamente');
          setHasPendingMap(true);
          setHasUploadedMap(true);
          setMapFile(null);
          setUploadProgress(0);
        },
        onError: (error) => {
          setUploadingMap(false);
          setUploadProgress(0);
          console.error('❌ Error en subida TUS:', error);
          alert('Error al subir el mapa: ' + error.message);
        },
      });

      upload.start();
    } catch (error) {
      setUploadingMap(false);
      setUploadProgress(0);
      console.error('❌ Error al obtener URL de subida:', error);
      alert('Error al iniciar la subida');
    }
  };

  // Download map from server
  const downloadMap = async () => {
    if (!id) return;

    setDownloadingMap(true);
    try {
      console.log('📥 Descargando mapa desde:', `${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/map/download`);

      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/map/download`, {
        credentials: 'include',
      });

      if (!res.ok) {
        const data = await res.json();
        alert(data.error || 'Error al descargar el mapa');
        return;
      }

      // Get the blob from response
      const blob = await res.blob();

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `world_${id}.tar.gz`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert('✅ Mapa descargado exitosamente');
    } catch (err) {
      console.error('❌ Error:', err);
      window.location.reload();
    } finally {
      setDownloadingMap(false);
    }
  };

  // Borrar mundo: llama a DELETE /api/servers/:id/world. El backend decide
  // si borra del contenedor (VPS activo) o del backup en R2 (VPS apagado).
  // ⚠️ Acción destructiva e irreversible — pedimos confirmación previa.
  const deleteWorld = async () => {
    if (!id || deletingWorld) return;

    const confirmed = window.confirm(
      '¿Borrar el mundo?\n\nEsta acción es permanente y no se puede deshacer. El mundo se regenerará al iniciar el servidor.'
    );
    if (!confirmed) return;

    setDeletingWorld(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/world`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        alert(data?.error || 'Error al borrar el mundo');
        setDeletingWorld(false);
        return;
      }

      const data = await res.json();
      alert('✅ Mundo borrado.');
      setShowModal(null); // cerrar el modal de mundos
    } catch (err) {
      console.error('❌ Error borrando mundo:', err);
      window.location.reload();
    } finally {
      setDeletingWorld(false);
    }
  };

  // Get icon for file type
  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const icons: Record<string, string> = {
      json: '📋',
      yml: '⚙️',
      yaml: '⚙️',
      properties: '🔧',
      cfg: '🔧',
      txt: '📝',
      toml: '⚙️',
      xml: '📰',
    };
    return icons[ext] || '📄';
  };

  // Get features for a server type
  const getFeaturesForServer = (serverType: string): string[] => {
    // Try to get game from server.game, fallback to serverType
    const gameName = (server?.game || serverType).toLowerCase();

    // Direct match
    if (serverFeatures[gameName as keyof typeof serverFeatures]) {
      return serverFeatures[gameName as keyof typeof serverFeatures]?.features || [];
    }

    // Try to find a matching key (for backwards compatibility)
    const gameMap: Record<string, string> = {
      'minecraft': 'minecraft',
      'counter strike': 'counter strike',
      'counterstrike': 'counter strike',
      'terraria': 'terraria',
    };

    const gameCategory = gameMap[gameName] || 'minecraft';
    const features = serverFeatures[gameCategory as keyof typeof serverFeatures];
    return features?.features || [];
  };

  // Get features that require an IP for a server type
  const getFeaturesRequiringIp = (serverType: string): string[] => {
    const gameName = (server?.game || serverType).toLowerCase();

    // Direct match
    if (serverFeatures[gameName as keyof typeof serverFeatures]) {
      return serverFeatures[gameName as keyof typeof serverFeatures]?.requiresIp || [];
    }

    // Try to find a matching key (for backwards compatibility)
    const gameMap: Record<string, string> = {
      'minecraft': 'minecraft',
      'counter strike': 'counter strike',
      'counterstrike': 'counter strike',
      'terraria': 'terraria',
    };

    const gameCategory = gameMap[gameName] || 'minecraft';
    const features = serverFeatures[gameCategory as keyof typeof serverFeatures];
    return features?.requiresIp || [];
  };

  // Format download count
  const formatDownloads = (count: number): string => {
    if (count >= 1000000) {
      return (count / 1000000).toFixed(1) + 'M';
    }
    if (count >= 1000) {
      return (count / 1000).toFixed(1) + 'K';
    }
    return count.toString();
  };

  // Helper function to get game display name from server type
  const getGameDisplayName = (serverType: string, serverGame?: string): string => {
    const gameMap: Record<string, string> = {
      '1.6': 'Counter Strike',
      'Source': 'Counter Strike',
      'terraria-vanilla': 'Terraria',
    };

    // First check if we can determine from server type
    if (gameMap[serverType]) {
      return gameMap[serverType];
    }

    // Then check if serverGame was explicitly set
    if (serverGame) return serverGame;

    // Fallback to capitalized server type
    return serverType.charAt(0).toUpperCase() + serverType.slice(1);
  };

  // Load plugins from server
  const loadPlugins = async () => {
    if (!id) return;

    setLoadingPlugins(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/plugins?server_id=${id}`, {
        credentials: 'include',
      });
      const data = await res.json();

      if (res.ok) {
        setPlugins(data.plugins || []);
      }
    } catch (err) {
      console.error('Error loading plugins:', err);
    } finally {
      setLoadingPlugins(false);
    }
  };

  // Search plugins on Modrinth
  const searchPlugins = async (loadMore = false) => {
    if (!searchQuery.trim() || !server) return;

    if (loadMore) {
      setLoadingMorePlugins(true);
    } else {
      setSearchingPlugins(true);
      setSearchResults([]);
      setPluginsPage(0);
      setHasMorePlugins(true);
    }

    try {
      // Get version and loader from server type
      const version = server.version;
      const loader = server.type;
      const offset = loadMore ? pluginsPage * 12 : 0;

      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/plugins/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          query: searchQuery,
          limit: 12,
          offset: offset,
          minecraft_version: version,
          loader: loader,
        }),
      });
      const data = await res.json();

      if (res.ok) {
        const newResults = data.results || [];
        if (loadMore) {
          setSearchResults(prev => [...prev, ...newResults]);
        } else {
          setSearchResults(newResults);
        }

        if (newResults.length < 12) {
          setHasMorePlugins(false);
        } else {
          setPluginsPage(prev => prev + 1);
        }
      } else {
        alert(data.detail || 'Error searching plugins');
      }
    } catch (err) {
      console.error('Error searching plugins:', err);
      window.location.reload();
    } finally {
      if (loadMore) {
        setLoadingMorePlugins(false);
      } else {
        setSearchingPlugins(false);
      }
    }
  };

  // Load popular plugins
  const loadPopularPlugins = async () => {
    if (!server) return;

    setLoadingPopularPlugins(true);
    try {
      const version = server.version;
      const loader = server.type;

      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/plugins/popular?minecraft_version=${version}&loader=${loader}`, {
        credentials: 'include',
      });
      const data = await res.json();

      if (res.ok) {
        setPopularPlugins(data.results || []);
      } else {
        console.error('Error loading popular plugins:', data.error);
      }
    } catch (err) {
      console.error('Error loading popular plugins:', err);
    } finally {
      setLoadingPopularPlugins(false);
    }
  };

  // Load mods from server
  const loadMods = async () => {
    if (!id) return;

    setLoadingMods(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/mods?server_id=${id}`, {
        credentials: 'include',
      });
      const data = await res.json();

      if (res.ok) {
        setMods(data.mods || []);
      }
    } catch (err) {
      console.error('Error loading mods:', err);
    } finally {
      setLoadingMods(false);
    }
  };

  // Search mods on Modrinth
  const searchMods = async (loadMore = false) => {
    if (!searchModQuery.trim() || !server) return;

    if (loadMore) {
      setLoadingMoreMods(true);
    } else {
      setSearchingMods(true);
      setSearchModResults([]);
      setModsPage(0);
      setHasMoreMods(true);
    }

    try {
      const version = server.version;
      const loader = server.type;
      const offset = loadMore ? modsPage * 12 : 0;

      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/mods/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          query: searchModQuery,
          limit: 12,
          offset: offset,
          minecraft_version: version,
          loader: loader,
        }),
      });
      const data = await res.json();

      if (res.ok) {
        const newResults = data.results || [];
        if (loadMore) {
          setSearchModResults(prev => [...prev, ...newResults]);
        } else {
          setSearchModResults(newResults);
        }

        if (newResults.length < 12) {
          setHasMoreMods(false);
        } else {
          setModsPage(prev => prev + 1);
        }
      } else {
        alert(data.detail || 'Error searching mods');
      }
    } catch (err) {
      console.error('Error searching mods:', err);
      window.location.reload();
    } finally {
      if (loadMore) {
        setLoadingMoreMods(false);
      } else {
        setSearchingMods(false);
      }
    }
  };

  // Load popular mods
  const loadPopularMods = async () => {
    if (!server) return;

    setLoadingPopularMods(true);
    try {
      const version = server.version;
      const loader = server.type;

      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/mods/popular?minecraft_version=${version}&loader=${loader}`, {
        credentials: 'include',
      });
      const data = await res.json();

      if (res.ok) {
        setPopularMods(data.results || []);
      } else {
        console.error('Error loading popular mods:', data.error);
      }
    } catch (err) {
      console.error('Error loading popular mods:', err);
    } finally {
      setLoadingPopularMods(false);
    }
  };

  // Clear search results when query is empty
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setPluginsPage(0);
      setHasMorePlugins(true);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (!searchModQuery.trim()) {
      setSearchModResults([]);
      setModsPage(0);
      setHasMoreMods(true);
    }
  }, [searchModQuery]);

  // Download mod from Modrinth
  const downloadMod = async (projectId: string) => {
    if (!id || !server) return;

    setDownloadingMod(projectId);
    try {
      const version = server.version;
      const loader = server.type;

      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/mods/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          project_id: projectId,
          minecraft_version: version,
          loader: loader,
          server_id: id,
        }),
      });
      const data = await res.json();

      if (res.ok) {
        setToastMessage(`${data.filename} instalado correctamente`);
        setShowToast(true);
        loadMods();
      } else {
        alert(data.detail || 'Error downloading mod');
      }
    } catch (err) {
      console.error('Error downloading mod:', err);
      window.location.reload();
    } finally {
      setDownloadingMod(null);
    }
  };

  // Delete mod from server
  const deleteMod = async (filename: string) => {
    if (!id) return;

    // Confirm deletion
    if (!confirm(`¿Estás seguro de que quieres eliminar el mod "${filename}"?`)) {
      return;
    }

    setDeletingMod(filename);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/mods?server_id=${id}&filename=${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();

      if (res.ok) {
        loadMods();
      } else {
        alert(data.detail || 'Error deleting mod');
      }
    } catch (err) {
      console.error('Error deleting mod:', err);
      window.location.reload();
    } finally {
      setDeletingMod(null);
    }
  };

  // Download plugin from Modrinth
  const downloadPlugin = async (projectId: string) => {
    if (!id || !server) return;

    setDownloadingPlugin(projectId);
    try {
      // Get version and loader from server type
      const version = server.version;
      const loader = server.type;

      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/plugins/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          project_id: projectId,
          minecraft_version: version,
          loader: loader,
          server_id: id,
        }),
      });
      const data = await res.json();

      if (res.ok) {
        setToastMessage(`${data.filename} instalado correctamente`);
        setShowToast(true);
        loadPlugins();
      } else {
        alert(data.detail || 'Error downloading plugin');
      }
    } catch (err) {
      console.error('Error downloading plugin:', err);
      window.location.reload();
    } finally {
      setDownloadingPlugin(null);
    }
  };

  // Delete plugin from server
  const deletePlugin = async (filename: string) => {
    if (!id) return;

    // Confirm deletion
    if (!confirm(`¿Estás seguro de que quieres eliminar el plugin "${filename}"?`)) {
      return;
    }

    setDeletingPlugin(filename);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/plugins?server_id=${id}&filename=${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();

      if (res.ok) {
        loadPlugins();
      } else {
        alert(data.detail || 'Error deleting plugin');
      }
    } catch (err) {
      console.error('Error deleting plugin:', err);
      window.location.reload();
    } finally {
      setDeletingPlugin(null);
    }
  };

  // Load whitelist from server using Minecraft files
  const loadWhitelist = async () => {
    if (!id) return;

    setLoadingWhitelist(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/minecraft/whitelist`, {
        credentials: 'include',
      });
      const data = await res.json();

      if (res.ok && data.players) {
        setWhitelist(data.players);
        setWhitelistEnabled(data.enabled || false);
      } else if (res.ok && data.content) {
        // Also handle the config endpoint response format
        try {
          const parsed = JSON.parse(data.content);
          const players = parsed.whitelist?.map((p: any) => p.name || p) || [];
          setWhitelist(players);
        } catch (e) {
          setWhitelist([]);
        }
      } else if (res.ok && !data.players && !data.content) {
        setWhitelist([]);
      }
    } catch (err) {
      console.error('Error loading whitelist:', err);
    } finally {
      setLoadingWhitelist(false);
    }
  };

  // Add player to whitelist
  const addToWhitelist = async (playerName: string) => {
    if (!id || !playerName.trim() || !server?.active) return;

    setAddingToWhitelist(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/minecraft/whitelist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'add', player: playerName.trim() }),
      });
      const data = await res.json();

      if (res.ok) {
        loadWhitelist();
      } else {
        alert(data.error || 'Error al añadir a la whitelist');
      }
    } catch (err) {
      console.error('Error adding to whitelist:', err);
      window.location.reload();
    } finally {
      setAddingToWhitelist(false);
    }
  };

  // Remove player from whitelist
  const removeFromWhitelist = async (playerName: string) => {
    if (!id || !server?.active) return;

    setRemovingFromWhitelist(playerName);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/minecraft/whitelist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'remove', player: playerName }),
      });
      const data = await res.json();

      if (res.ok) {
        loadWhitelist();
      } else {
        alert(data.error || 'Error al eliminar de la whitelist');
      }
    } catch (err) {
      console.error('Error removing from whitelist:', err);
      window.location.reload();
    } finally {
      setRemovingFromWhitelist(null);
    }
  };

  // Toggle whitelist on/off
  const toggleWhitelist = async () => {
    if (!id || whitelistToggling) return;

    const newState = !whitelistEnabled;
    setWhitelistToggling(true);

    // Optimistic UI update
    setWhitelistEnabled(newState);

    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/minecraft/whitelist/toggle`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: newState }),
      });
      const data = await res.json();

      if (res.ok) {
        // Check if command was executed successfully when server is active
        if (server?.active && data.commandExecuted === false) {
          alert(`⚠️ Whitelist ${newState ? 'activada' : 'desactivada'} en configuración, pero el comando no se envió al servidor. ${data.commandError || ''}`);
        }
      } else {
        // Revert on error
        setWhitelistEnabled(!newState);
        alert(data.error || 'Error al cambiar estado de whitelist');
      }
    } catch (err) {
      console.error('Error toggling whitelist:', err);
      // Revert on error
      setWhitelistEnabled(!newState);
      window.location.reload();
    } finally {
      setWhitelistToggling(false);
    }
  };

  // Change Minecraft server version via PUT /api/servers/:id/minecraft/version
  const handleChangeVersion = async () => {
    if (!id || !selectedVersion) return;

    setChangingVersion(true);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/minecraft/version`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ version: selectedVersion }),
        }
      );
      const data = await res.json();

      if (res.ok) {
        const newVersion = data.server?.version || selectedVersion;
        // Update local server state and the cached version in context
        setServer((prev) => (prev ? { ...prev, version: newVersion } : prev));
        if (typeof updateServer === 'function') {
          updateServer(id, { version: newVersion });
        }

        // Si el server está activo, el cambio queda pendiente en el
        // contenedor hasta que el usuario reinicie. Marcamos el flag
        // para mostrar el banner.
        if (server?.active) {
          setPendingSoftwareChange((prev) => ({
            ...(prev ?? {}),
            version: newVersion,
          }));
          setToastMessage(`Versión actualizada a ${newVersion}. Reiniciá el servidor para aplicar el cambio.`);
        } else {
          setToastMessage(`Versión actualizada a ${newVersion}`);
        }
        setShowToast(true);
        setPendingLatestVersion(null);
        setShowModal(null);
      } else {
        alert(data.error || 'Error al cambiar la versión');
      }
    } catch (err) {
      console.error('Error changing version:', err);
      window.location.reload();
    } finally {
      setChangingVersion(false);
    }
  };

  // Change Minecraft server software (type) via PUT /api/servers/:id/minecraft/type
  const handleChangeType = async () => {
    if (!id || !selectedType) return;

    const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
    const newType = selectedType;

    // Decide if a version-modal follow-up is needed BEFORE we close the
    // type modal, so we can chain modals without flicker.
    const versionsForType = (state.versions?.['Minecraft']?.[newType] || []) as Array<{
      id: string;
      name?: string;
    }>;
    const latestForType = versionsForType[0]?.id || null;
    const currentVersionCompatible = latestForType
      ? versionsForType.some((v) => v.id === server?.version)
      : true;
    const needsVersionModal = latestForType && !currentVersionCompatible;

    // Optimistic UI: close the type modal immediately so the user does
    // NOT see a "Guardando..." button label. Local state and the AppContext
    // are updated optimistically; the PUT runs in the background. If it
    // fails we revert and surface an alert.
    setServer((prev) => (prev ? { ...prev, type: newType } : prev));
    if (typeof updateServer === 'function') {
      updateServer(id, { type: newType });
    }
    setPendingLatestVersion(needsVersionModal ? latestForType : null);
    setShowModal(needsVersionModal ? 'changeVersion' : null);

    try {
      const res = await fetch(
        `${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/minecraft/type`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ type: newType }),
        }
      );
      const data = await res.json();

      if (res.ok) {
        // Si el server está activo, el cambio queda pendiente en el
        // contenedor hasta que el usuario reinicie. Marcamos el flag
        // para mostrar el banner.
        if (server?.active) {
          setPendingSoftwareChange((prev) => ({
            ...(prev ?? {}),
            type: newType,
          }));
          setToastMessage(`Software actualizado a ${capitalize(newType)}. Reiniciá el servidor para aplicar el cambio.`);
        } else {
          setToastMessage(`Software actualizado a ${capitalize(newType)}`);
        }
        setShowToast(true);
      } else {
        // Revert optimistic state on failure
        setServer(server); // restore previous Server snapshot
        if (typeof updateServer === 'function') {
          updateServer(id, { type: server?.type });
        }
        setShowModal('changeType'); // reopen the modal so the user can retry
        alert(data.error || 'Error al cambiar el software');
      }
    } catch (err) {
      console.error('Error changing software:', err);
      // Revert + reopen
      setServer(server);
      if (typeof updateServer === 'function') {
        updateServer(id, { type: server?.type });
      }
      setShowModal('changeType');
      window.location.reload();
    }
  };

  // Helper: cerrar el modal de versión. Si hay una versión pendiente de
  // auto-aplicar (flujo tras cambio de software), la aplica antes de cerrar.
  // Devuelve true cuando se aplicó la versión pendiente.
  const closeVersionModal = async (): Promise<boolean> => {
    if (pendingLatestVersion && id) {
      const versionToApply = pendingLatestVersion;
      setPendingLatestVersion(null);
      setShowModal(null);
      setChangingVersion(true);
      try {
        const res = await fetch(
          `${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/minecraft/version`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ version: versionToApply }),
          }
        );
        const data = await res.json();
        if (res.ok) {
          const newVersion = data.server?.version || versionToApply;
          setServer((prev) => (prev ? { ...prev, version: newVersion } : prev));
          if (typeof updateServer === 'function') {
            updateServer(id, { version: newVersion });
          }
          setToastMessage(`Versión establecida a ${newVersion} (por defecto del nuevo software)`);
          setShowToast(true);
        }
        // Si falla, no mostramos alert aquí para no spamear al usuario que
        // solo quería cerrar el modal. El cambio de software ya quedó hecho.
      } catch {
        // Silenciar errores de red en este flujo automático.
      } finally {
        setChangingVersion(false);
      }
      return true;
    }
    setShowModal(null);
    return false;
  };

  // Load blacklist (banlist) from server using Minecraft files
  const loadBlacklist = async () => {
    if (!id) return;

    setLoadingBlacklist(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/minecraft/blacklist`, {
        credentials: 'include',
      });
      const data = await res.json();

      if (res.ok && data.players) {
        setBlacklist(data.players);
      } else if (res.ok && data.content) {
        // Also handle the config endpoint response format
        try {
          const parsed = JSON.parse(data.content);
          const players = parsed.users?.map((p: any) => p.name) || [];
          setBlacklist(players);
        } catch (e) {
          setBlacklist([]);
        }
      } else if (res.ok && !data.players && !data.content) {
        setBlacklist([]);
      }
    } catch (err) {
      console.error('Error loading blacklist:', err);
    } finally {
      setLoadingBlacklist(false);
    }
  };

  // Add player to blacklist (ban)
  const addToBlacklist = async (playerName: string) => {
    if (!id || !playerName.trim() || !server?.active) return;

    setAddingToBlacklist(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/minecraft/blacklist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'add', player: playerName.trim() }),
      });
      const data = await res.json();

      if (res.ok) {
        loadBlacklist();
      } else {
        alert(data.error || 'Error al añadir a la blacklist');
      }
    } catch (err) {
      console.error('Error adding to blacklist:', err);
      window.location.reload();
    } finally {
      setAddingToBlacklist(false);
    }
  };

  // Remove player from blacklist (unban)
  const removeFromBlacklist = async (playerName: string) => {
    if (!id || !server?.active) return;

    setRemovingFromBlacklist(playerName);
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/minecraft/blacklist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'remove', player: playerName }),
      });
      const data = await res.json();

      if (res.ok) {
        loadBlacklist();
      } else {
        alert(data.error || 'Error al eliminar de la blacklist');
      }
    } catch (err) {
      console.error('Error removing from blacklist:', err);
      window.location.reload();
    } finally {
      setRemovingFromBlacklist(null);
    }
  };

  const [copied, setCopied] = useState(false);
  const [ftpCopied, setFtpCopied] = useState(false);
  const [ftpPasswordVisible, setFtpPasswordVisible] = useState(false);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      // Resetear el estado de "copiado" después de 2 segundos
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error al copiar: ', err);
    }
  };

  const [shareCopied, setShareCopied] = useState(false);

  const handleShare = async () => {
    if (!server) return;
    const address = getServerAddress(server);
    if (!address) return;
    const message = [
      `🎮 ¡Únete a mi servidor de ${server.game} en ${APP_NAME}!`,
      `Dirección: ${address}`,
    ].join('\n');
    try {
      await navigator.clipboard.writeText(message);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch (err) {
      console.error('Error al compartir: ', err);
    }
  };

  const handleCopyFtp = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setFtpCopied(true);
      setTimeout(() => setFtpCopied(false), 2000);
    } catch (err) {
      console.error('Error al copiar: ', err);
    }
  };

  // Check if content has been modified
  const isContentModified = selectedConfigFile && editedContent !== selectedConfigFile.content;

  // Manejar click en Properties
  const handlePropertiesClick = () => {
    // Reset config data to force re-fetch
    setConfigData(null);
    setSelectedConfigFile(null);
    setEditedContent('');
    setShowModal('properties');
  };

  useEffect(() => {
    if (showPermissionsModal) {
      fetchPermissions();
    }
  }, [showPermissionsModal, fetchPermissions]);

  if (loading) {
    return (
      <div className="server-panel-page center-loading-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  if (error || !server) {
    return (
      <div className="server-panel-page">
        <div className="error-card">
          <p>{error || 'Servidor no encontrado'}</p>
          <button onClick={() => navigate('/dashboard')} className="back-button">
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sp-shell">
      {/* ============================================================
          SIDEBAR
          ============================================================ */}
      <aside className="sp-sidebar" aria-label="Navegación del servidor">
        <Link to="/dashboard" className="sp-sidebar-back" aria-label="Volver al inicio">
          <ArrowLeft size={18} className="sp-sidebar-back-icon" />
          <span className="sp-sidebar-back-label">Voltary</span>
        </Link>

        <nav className="sp-sidebar-nav">
          {/* ---- Estado ---- */}
          <div className="sp-sidebar-section">
            <span className="sp-sidebar-section-title">Estado</span>
            <button
              type="button"
              className="sp-sidebar-item active"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <LayoutDashboard size={18} className="sp-sidebar-item-icon" />
              <span className="sp-sidebar-item-label">Resumen</span>
            </button>

            {getFeaturesForServer(server.type).includes('console') && (
              getFeaturesRequiringIp(server.type).includes('console') && !server.provisioned ? (
                <button type="button" className="sp-sidebar-item" disabled title="El servidor tiene que encender al menos una vez">
                  <Terminal size={18} className="sp-sidebar-item-icon" />
                  <span className="sp-sidebar-item-label">Consola</span>
                </button>
              ) : (
                <Link to={`/server/${server.id}/console`} className="sp-sidebar-item">
                  <Terminal size={18} className="sp-sidebar-item-icon" />
                  <span className="sp-sidebar-item-label">Consola</span>
                </Link>
              )
            )}
          </div>

          {/* ---- Gestión ---- */}
          <div className="sp-sidebar-section">
            <span className="sp-sidebar-section-title">Gestión</span>

            {getFeaturesForServer(server.type).includes('properties') && (
              getFeaturesRequiringIp(server.type).includes('properties') && !server.provisioned ? (
                <button type="button" className="sp-sidebar-item" disabled title="El servidor tiene que encender al menos una vez">
                  <Settings size={18} className="sp-sidebar-item-icon" />
                  <span className="sp-sidebar-item-label">Archivos</span>
                </button>
              ) : (
                <button type="button" className="sp-sidebar-item" onClick={handlePropertiesClick}>
                  <Settings size={18} className="sp-sidebar-item-icon" />
                  <span className="sp-sidebar-item-label">Archivos</span>
                </button>
              )
            )}

            {getFeaturesForServer(server.type).includes('plugins') && ['paper', 'spigot'].includes(server.type) && (
              getFeaturesRequiringIp(server.type).includes('plugins') && !server.provisioned ? (
                <button type="button" className="sp-sidebar-item" disabled title="El servidor tiene que encender al menos una vez">
                  <Puzzle size={18} className="sp-sidebar-item-icon" />
                  <span className="sp-sidebar-item-label">Plugins</span>
                </button>
              ) : (
                <button type="button" className="sp-sidebar-item" onClick={() => { setShowModal('plugins'); loadPlugins(); loadPopularPlugins(); }}>
                  <Puzzle size={18} className="sp-sidebar-item-icon" />
                  <span className="sp-sidebar-item-label">Plugins</span>
                  {plugins.length > 0 && <span className="sp-sidebar-item-badge">{plugins.length}</span>}
                </button>
              )
            )}

            {(getFeaturesForServer(server.type).includes('mods') || ['forge', 'fabric'].includes(server.type)) && (
              <button type="button" className="sp-sidebar-item" onClick={() => { setShowModal('mods'); loadMods(); loadPopularMods(); }}>
                <Package size={18} className="sp-sidebar-item-icon" />
                <span className="sp-sidebar-item-label">Mods</span>
                {mods.length > 0 && <span className="sp-sidebar-item-badge">{mods.length}</span>}
              </button>
            )}

            {getFeaturesForServer(server.type).includes('worlds') && (
              <button type="button" className="sp-sidebar-item" onClick={() => setShowModal('worlds')}>
                <Earth size={18} className="sp-sidebar-item-icon" />
                <span className="sp-sidebar-item-label">Mundos</span>
              </button>
            )}

            {getFeaturesForServer(server.type).includes('maps') && (
              <button type="button" className="sp-sidebar-item" onClick={handleMapsClick}>
                <MapIcon size={18} className="sp-sidebar-item-icon" />
                <span className="sp-sidebar-item-label">Mapas</span>
              </button>
            )}

            {getFeaturesForServer(server.type).includes('whitelist') && (
              <button type="button" className="sp-sidebar-item" onClick={() => { setShowModal('whitelist'); loadWhitelist(); }}>
                <BadgeCheck size={18} className="sp-sidebar-item-icon" />
                <span className="sp-sidebar-item-label">Whitelist</span>
              </button>
            )}

            {getFeaturesForServer(server.type).includes('blacklist') && (
              <button type="button" className="sp-sidebar-item" onClick={() => { setShowModal('blacklist'); loadBlacklist(); }}>
                <CircleX size={18} className="sp-sidebar-item-icon" />
                <span className="sp-sidebar-item-label">Blacklist</span>
              </button>
            )}
          </div>

          {/* ---- Ajustes (owner) ---- */}
          {server.permissions === 'owner' && (
            <div className="sp-sidebar-section">
              <span className="sp-sidebar-section-title">Ajustes</span>

              {server.game === 'Minecraft' && (
                <button
                  type="button"
                  className="sp-sidebar-item"
                  onClick={() => {
                    setSelectedVersion(server.version || '');
                    setPendingLatestVersion(null);
                    setShowModal('changeVersion');
                  }}
                >
                  <Tag size={18} className="sp-sidebar-item-icon" />
                  <span className="sp-sidebar-item-label">Versión</span>
                </button>
              )}

              {server.game === 'Minecraft' && (
                <button
                  type="button"
                  className="sp-sidebar-item"
                  onClick={() => {
                    setSelectedType(server.type || '');
                    setPendingLatestVersion(null);
                    setShowModal('changeType');
                  }}
                >
                  <Cpu size={18} className="sp-sidebar-item-icon" />
                  <span className="sp-sidebar-item-label">Software</span>
                </button>
              )}

              <button
                type="button"
                className="sp-sidebar-item"
                onClick={() => {
                  if (sessionActive) {
                    setImportantAlertMessage('No se puede cambiar de plan mientras la sesión esté activa.');
                    setShowImportantAlert(true);
                    return;
                  }
                  handleStartWithPlan();
                }}
              >
                <Settings size={18} className="sp-sidebar-item-icon" />
                <span className="sp-sidebar-item-label">Plan</span>
              </button>

              <button
                type="button"
                className="sp-sidebar-item"
                onClick={() => { setShowPermissionsModal(true); fetchPermissions(); }}
              >
                <Users size={18} className="sp-sidebar-item-icon" />
                <span className="sp-sidebar-item-label">Colaboradores</span>
                {permissionsList.length > 0 && <span className="sp-sidebar-item-badge">{permissionsList.length}</span>}
              </button>
            </div>
          )}

          {/* ---- Zona peligrosa ---- */}
          {server.permissions === 'owner' && !server.active && (
            <div className="sp-sidebar-section">
              <span className="sp-sidebar-section-title">Zona peligrosa</span>
              <button
                type="button"
                className="sp-sidebar-item danger"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 size={18} className="sp-sidebar-item-icon" />
                <span className="sp-sidebar-item-label">Eliminar</span>
              </button>
            </div>
          )}
        </nav>
      </aside>

      {/* ============================================================
          MAIN CONTENT
          ============================================================ */}
      <main className="sp-main">
        {/* ---- Topbar (account nav) ---- */}
        <header className="sp-topbar">
          <div className="sp-topbar-left">
            <Link to="/dashboard" className="sp-topbar-brand">
              <LayoutDashboard size={16} />
              <span>Dashboard</span>
            </Link>
            <span className="sp-topbar-divider" aria-hidden="true" />
            <span className="sp-topbar-crumb">Servidor</span>
          </div>
          <div className="sp-topbar-right">
            <LogoutButton />
          </div>
        </header>

        {/* ---- Banner de estado (transición) ---- */}
        {(showStartingModal || showSavingModal || showRestartingModal) && (
          <div className={`sp-banner ${showSavingModal ? 'sp-banner--warning' : 'sp-banner--info'}`} role="status" aria-live="polite">
            <span className="sp-banner-icon" aria-hidden="true">
              {showSavingModal ? <Save size={16} /> : showRestartingModal ? <RotateCw size={16} /> : <Loader2 size={16} className="sp-spin" />}
            </span>
            <span className="sp-banner-text">
              {showSavingModal
                ? (server?.game === 'Minecraft' ? 'Guardando mundo y deteniendo el servidor…' : 'Apagando servidor…')
                : showRestartingModal
                  ? 'Reiniciando servidor…'
                  : `Servidor iniciando… ${formatTime(activeTime)}`}
            </span>
          </div>
        )}

        {/* ---- Banner de cambio de software pendiente ---- */}
        {/* Sale cuando el usuario cambia tipo o versión mientras el server
            está activo: la DB se actualiza pero el contenedor sigue
            corriendo con el software viejo. Se limpia cuando llega el SSE
            state='restarting' (porque el restart aplica el cambio). */}
        {pendingSoftwareChange && server?.active && !showRestartingModal && (
          <div className="sp-banner sp-banner--warning" role="status" aria-live="polite">
            <span className="sp-banner-icon" aria-hidden="true">
              <Info size={16} />
            </span>
            <span className="sp-banner-text">
              {pendingSoftwareChange.type && pendingSoftwareChange.version
                ? `Cambio de software pendiente: ${pendingSoftwareChange.type} ${pendingSoftwareChange.version}. Reiniciá para aplicar.`
                : pendingSoftwareChange.type
                  ? `Cambio de software pendiente: ${pendingSoftwareChange.type}. Reiniciá para aplicar.`
                  : `Cambio de versión pendiente: ${pendingSoftwareChange.version}. Reiniciá para aplicar.`}
            </span>
            <button
              type="button"
              className="sp-banner-action"
              onClick={handleRestart}
              disabled={restarting}
            >
              <RotateCw size={14} />
              <span>Reiniciar ahora</span>
            </button>
          </div>
        )}

      <header className="sp-page-header">
        <div className="sp-page-title-group">
          <div className="sp-page-title">
            {isEditingName ? (
              <div className="sp-form-row" style={{ marginTop: '0.25rem' }}>
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value.slice(0, 34))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                    if (e.key === 'Escape') setIsEditingName(false);
                  }}
                  maxLength={34}
                  autoFocus
                  className="sp-input"
                  style={{ maxWidth: '20rem' }}
                />
                <button onClick={handleSaveName} disabled={savingName || !editedName.trim()} className="btn btn-primary btn-sm" type="button">
                  {savingName ? '…' : <Check size={14} />}
                </button>
                <button onClick={() => setIsEditingName(false)} className="btn btn-ghost btn-sm" type="button">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <h1
                onClick={server.permissions === 'owner' ? startEditingName : undefined}
                style={{ cursor: server.permissions === 'owner' ? 'text' : 'default' }}
                title={server.permissions === 'owner' ? 'Clic para cambiar el nombre' : ''}
              >
                {server.name}
              </h1>
            )}
            <div className="sp-page-title-row">
              <span className={`sp-status sp-status--${server.status === 'online' ? 'online' : server.status === 'maintenance' ? 'maintenance' : 'offline'}`}>
                <span className="sp-status-dot" />
                {server.status === 'online' ? 'En línea' : server.status === 'maintenance' ? 'Mantenimiento' : 'Apagado'}
              </span>
              <span className="sp-page-title-divider" />
              <span className="sp-page-title-meta">
                <Gamepad2 size={12} />
                {server.game}
              </span>
              <span className="sp-page-title-divider" />
              <span className="sp-page-title-meta">v{server.version}</span>
              <span className="sp-page-title-divider" />
              <span className="sp-page-title-meta">{server.type ? server.type.charAt(0).toUpperCase() + server.type.slice(1) : ''}</span>
            </div>
          </div>
        </div>
        <div className="sp-page-actions">
          {sessionActive ? (
            <button
              type="button"
              onClick={handleStopSession}
              className="sp-action-btn sp-action-btn--danger"
              disabled={powering || shuttingDown}
            >
              <PowerOff size={16} />
              <span className="sp-action-btn-label">Detener Sesión</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => { !serverPlanInfo?.plan ? handleStartWithPlan() : handleTogglePower(); }}
              className="sp-action-btn sp-action-btn--primary"
              disabled={powering || shuttingDown}
            >
              <Zap size={16} />
              <span className="sp-action-btn-label">
                {powering ? 'Cargando…' : !serverPlanInfo?.plan ? 'Seleccionar Plan' : 'Iniciar Sesión'}
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={handleRestart}
            className="sp-action-btn sp-action-btn--ghost"
            disabled={restarting || shuttingDown || !sessionActive}
            title={!sessionActive ? 'Inicia una sesión para reiniciar' : 'Reiniciar el servidor'}
          >
            <RotateCw size={16} />
            <span className="sp-action-btn-label">Reiniciar</span>
          </button>
        </div>
      </header>

      {/* ============================================================
          GRID DE PANELES
          ============================================================ */}
      <div className="sp-grid">

        {/* ---- Panel: Estado / Energía ---- */}
        <section className="sp-panel sp-col-4 sp-col-sm-12">
          <div className="sp-panel-header">
            <Power className="sp-panel-icon" />
            <h2 className="sp-panel-title">Estado</h2>
            {sessionActive && <span className="sp-pill sp-pill--accent">SESIÓN</span>}
          </div>
          <div className="sp-panel-body">
            <div className="sp-power-status">
              <div className={`sp-power-indicator ${server.active ? 'sp-power-indicator--active' : ''}`}>
                {server.active ? <Zap size={32} /> : <Power size={32} />}
              </div>
              <span className={`sp-power-text ${server.active ? 'sp-power-text--active' : ''}`}>
                {server.active ? 'Servidor Encendido' : 'Servidor Apagado'}
              </span>
              {server.active && (
                <div className="sp-power-time">
                  <span className="sp-power-time-label">Sesión actual</span>
                  <span className="sp-power-time-value">{formatTime(activeTime)}</span>
                </div>
              )}
              <div className="sp-power-time">
                <span className="sp-power-time-label">Total activo</span>
                <span className="sp-power-time-value">{formatTime(server.seconds_active || 0)}</span>
              </div>
            </div>

            <div className="sp-power-controls">
              {sessionActive ? (
                <>
                  <div className="sp-power-controls-row">
                    {(() => {
                      const stopIsLatest = lastStopDockerTime !== null && (lastStartDockerTime === null || lastStopDockerTime > lastStartDockerTime);
                      const containerOff = !server.active || stopIsLatest;
                      const isTransitioning = shuttingDown || shutdownCountdown !== null;
                      return (
                        <button
                          type="button"
                          onClick={containerOff ? handleStartContainer : handleShutdownServer}
                          className={`sp-power-btn ${containerOff ? 'sp-power-btn--on' : 'sp-power-btn--shutdown'}`}
                          disabled={shuttingDown || powering || startingContainer}
                          title={containerOff ? 'Vuelve a encender el contenedor del juego' : 'Apaga el servidor pero la sesión sigue activa y facturando'}
                        >
                          {startingContainer
                            ? 'Encendiendo...'
                            : isTransitioning
                              ? `Apagando… (${shutdownCountdown ?? 0}s)`
                              : containerOff
                                ? <><Zap size={14} /> Encender</>
                                : <><PowerOff size={14} /> Apagar</>}
                        </button>
                      );
                    })()}
                    <button
                      type="button"
                      onClick={handleRestart}
                      className="sp-power-btn sp-power-btn--restart"
                      disabled={restarting || shuttingDown || shutdownCountdown !== null || !server.active}
                      title={!server.active ? 'El servidor debe estar encendido para reiniciarlo' : 'Reiniciar el servidor'}
                    >
                      <RotateCw size={14} /> {restarting ? 'Reiniciando…' : 'Reiniciar'}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleStopSession}
                    className="sp-power-btn sp-power-btn--stop sp-power-btn--full"
                    disabled={powering || shuttingDown}
                  >
                    <PowerOff size={14} /> Detener Sesión
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => { !serverPlanInfo?.plan ? handleStartWithPlan() : handleTogglePower(); }}
                  className="sp-power-btn sp-power-btn--on sp-power-btn--full"
                  disabled={powering || shuttingDown}
                >
                  <Zap size={14} /> {powering ? 'Cargando…' : !serverPlanInfo?.plan ? 'Seleccionar Plan' : 'Iniciar Sesión'}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* ---- Panel: Recursos (live) ---- */}
        <section className="sp-panel sp-col-4 sp-col-sm-12">
          <div className="sp-panel-header">
            <Cpu className="sp-panel-icon" />
            <h2 className="sp-panel-title">Recursos</h2>
            {server.active && <span className="sp-pill sp-pill--success">● EN VIVO</span>}
          </div>
          <div className="sp-panel-body sp-panel-body--flush">
            <StatsPanel serverId={id!} isActive={!!server.active && !startingModalOpen} showHeader={false} />
          </div>
        </section>

        {/* ---- Panel: Información ---- */}
        <section className="sp-panel sp-col-4 sp-col-sm-12">
          <div className="sp-panel-header">
            <Info className="sp-panel-icon" />
            <h2 className="sp-panel-title">Información</h2>
          </div>
          <div className="sp-panel-body">
            <div className="sp-info-list">
              <div className="sp-info-row">
                <span className="sp-info-row-label">Dirección</span>
                <span className="sp-info-row-value">
                  {getServerAddress(server) ? (
                    <>
                      <code>{getServerAddress(server)}</code>
                      {server.permissions === 'owner' && (
                        <button
                          type="button"
                          onClick={handleOpenChangeSubdomain}
                          className="sp-copy-btn"
                          title="Cambiar el subdominio"
                          aria-label="Cambiar el subdominio"
                        >
                          <RotateCw size={14} />
                        </button>
                      )}
                      <button
                        className={`sp-copy-btn ${copied ? 'copied' : ''}`}
                        onClick={() => handleCopy(getServerAddress(server) ?? "")}
                        title="Copiar dirección"
                        aria-label="Copiar dirección"
                      >
                        <Clipboard size={14} />
                      </button>
                    </>
                  ) : <span className="sp-info-empty">No asignada</span>}
                </span>
              </div>
              <div className="sp-info-row">
                <span className="sp-info-row-label">Juego</span>
                <span className="sp-info-row-value">{server.game}</span>
              </div>
              {server.game === "Minecraft" && (
                <div className="sp-info-row">
                  <span className="sp-info-row-label">Software</span>
                  <span className="sp-info-row-value">
                    {server.type ? server.type.charAt(0).toUpperCase() + server.type.slice(1) : ''}
                    {server.permissions === 'owner' && (
                      <button
                        type="button"
                        onClick={() => { setSelectedType(server.type || ''); setPendingLatestVersion(null); setShowModal('changeType'); }}
                        className="sp-info-link"
                      >
                        <Cpu size={12} /> Cambiar
                      </button>
                    )}
                  </span>
                </div>
              )}
              <div className="sp-info-row">
                <span className="sp-info-row-label">Versión</span>
                <span className="sp-info-row-value">
                  {server.version}
                  {server.game === 'Minecraft' && server.permissions === 'owner' && (
                    <button
                      type="button"
                      onClick={() => { setSelectedVersion(server.version || ''); setPendingLatestVersion(null); setShowModal('changeVersion'); }}
                      className="sp-info-link"
                    >
                      <Tag size={12} /> Cambiar
                    </button>
                  )}
                </span>
              </div>
              {serverPlanInfo?.plan && (
                <>
                  <div className="sp-info-row">
                    <span className="sp-info-row-label">Plan</span>
                    <span className="sp-info-row-value">
                      <span className="sp-info-plan-tag">
                        {serverPlanInfo.plan.label}{'   '}
                        <span
                          role="img"
                          aria-label={serverPlanInfo.plan.location
                            ? `Ubicación ${serverPlanInfo.plan.location.toUpperCase()}`
                            : 'Ubicación no disponible'}
                          title={serverPlanInfo.plan.location?.toUpperCase()}
                        >
                          <LocationFlag location={serverPlanInfo.plan.location} />
                        </span>
                      </span>
                      {server.permissions === 'owner' && (
                        <button
                          type="button"
                          onClick={() => {
                            if (sessionActive) {
                              setImportantAlertMessage('No se puede cambiar de plan mientras la sesión esté activa.');
                              setShowImportantAlert(true);
                              return;
                            }
                            handleStartWithPlan();
                          }}
                          className="sp-info-link"
                          title="Cambiar el plan de este servidor"
                        >
                          <Settings size={12} /> Cambiar
                        </button>
                      )}
                    </span>
                  </div>
                  <div className="sp-info-row">
                    <span className="sp-info-row-label">Precio/hora</span>
                    <span className="sp-info-row-value sp-info-price">${serverPlanInfo.plan.price.toFixed(3)}</span>
                  </div>
                </>
              )}
            </div>
            {getServerAddress(server) && (
              <button
                type="button"
                className={`sp-share-btn ${shareCopied ? 'copied' : ''}`}
                onClick={handleShare}
                title="Copiar mensaje de invitación al portapapeles"
              >
                {shareCopied ? (
                  <><Check size={16} /> ¡Mensaje copiado!</>
                ) : (
                  <><Share2 size={16} /> Compartir servidor</>
                )}
              </button>
            )}
          </div>
        </section>

        {/* ---- Panel: Accesos rápidos ---- */}
        {server.permissions !== 'on/off' && (
          <section className="sp-panel sp-col-12">
            <div className="sp-panel-header">
              <SquareChevronRight className="sp-panel-icon" />
              <h2 className="sp-panel-title">Accesos rápidos</h2>
            </div>
            <div className="sp-panel-body">
              <div className="sp-actions-grid">
                {getFeaturesForServer(server.type).includes('console') && (
                  getFeaturesRequiringIp(server.type).includes('console') && !server.provisioned ? (
                    <button type="button" className="sp-action-tile" disabled title="El servidor tiene que encender al menos una vez">
                      <div className="sp-action-tile-icon"><Terminal size={18} /></div>
                      <span className="sp-action-tile-label">Consola</span>
                    </button>
                  ) : (
                    <Link to={`/server/${server.id}/console`} className="sp-action-tile">
                      <div className="sp-action-tile-icon"><Terminal size={18} /></div>
                      <span className="sp-action-tile-label">Consola</span>
                    </Link>
                  )
                )}
                {getFeaturesForServer(server.type).includes('properties') && (
                  getFeaturesRequiringIp(server.type).includes('properties') && !server.provisioned ? (
                    <button type="button" className="sp-action-tile" disabled title="El servidor tiene que encender al menos una vez">
                      <div className="sp-action-tile-icon"><Settings size={18} /></div>
                      <span className="sp-action-tile-label">Archivos</span>
                      <span className="sp-action-tile-desc">Config del servidor</span>
                    </button>
                  ) : (
                    <button type="button" onClick={handlePropertiesClick} className="sp-action-tile">
                      <div className="sp-action-tile-icon"><Settings size={18} /></div>
                      <span className="sp-action-tile-label">Archivos</span>
                      <span className="sp-action-tile-desc">Config del servidor</span>
                    </button>
                  )
                )}
                {getFeaturesForServer(server.type).includes('plugins') && ['paper', 'spigot'].includes(server.type) && (
                  <button type="button" onClick={() => { setShowModal('plugins'); loadPlugins(); loadPopularPlugins(); }} className="sp-action-tile">
                    <div className="sp-action-tile-icon"><Puzzle size={18} /></div>
                    <span className="sp-action-tile-label">Plugins</span>
                    <span className="sp-action-tile-desc">Modrinth</span>
                  </button>
                )}
                {(getFeaturesForServer(server.type).includes('mods') || ['forge', 'fabric'].includes(server.type)) && (
                  <button type="button" onClick={() => { setShowModal('mods'); loadMods(); loadPopularMods(); }} className="sp-action-tile">
                    <div className="sp-action-tile-icon"><Package size={18} /></div>
                    <span className="sp-action-tile-label">Mods</span>
                    <span className="sp-action-tile-desc">Modrinth</span>
                  </button>
                )}
                {getFeaturesForServer(server.type).includes('worlds') && (
                  <button type="button" onClick={() => setShowModal('worlds')} className="sp-action-tile">
                    <div className="sp-action-tile-icon"><Earth size={18} /></div>
                    <span className="sp-action-tile-label">Mundos</span>
                    <span className="sp-action-tile-desc">Subir / descargar</span>
                  </button>
                )}
                {getFeaturesForServer(server.type).includes('maps') && (
                  <button type="button" onClick={handleMapsClick} className="sp-action-tile">
                    <div className="sp-action-tile-icon"><MapIcon size={18} /></div>
                    <span className="sp-action-tile-label">Mapas</span>
                    <span className="sp-action-tile-desc">Cambiar mapa</span>
                  </button>
                )}
                {getFeaturesForServer(server.type).includes('whitelist') && (
                  <button type="button" onClick={() => { setShowModal('whitelist'); loadWhitelist(); }} className="sp-action-tile">
                    <div className="sp-action-tile-icon"><BadgeCheck size={18} /></div>
                    <span className="sp-action-tile-label">Whitelist</span>
                    <span className="sp-action-tile-desc">Lista blanca</span>
                  </button>
                )}
                {getFeaturesForServer(server.type).includes('blacklist') && (
                  <button type="button" onClick={() => { setShowModal('blacklist'); loadBlacklist(); }} className="sp-action-tile">
                    <div className="sp-action-tile-icon"><CircleX size={18} /></div>
                    <span className="sp-action-tile-label">Blacklist</span>
                    <span className="sp-action-tile-desc">Baneados</span>
                  </button>
                )}
              </div>
            </div>
          </section>
        )}

        {/* ---- Panel: FTP (solo si hay contraseña) ---- */}
        {server.ftp_password && (
          <section className="sp-panel sp-col-12">
            <div className="sp-panel-header">
              <Folder className="sp-panel-icon" />
              <h2 className="sp-panel-title">Acceso FTP</h2>
              <span className="sp-pill sp-pill--mono">SFTP / FTPS</span>
            </div>
            <div className="sp-panel-body">
              <div className="sp-ftp-grid">
                <div className="sp-ftp-field">
                  <span className="sp-ftp-field-label">Servidor</span>
                  <div className="sp-ftp-field-value">
                    {/* Mostramos la IP del VPS directamente — ya no dependemos
                        del *.node.voltary.org DNS para que el SFTP/FTPS
                        funcione, así que no hay requisito de DNS propio. */}
                    <code>{getServerAddress(server) || '—'}</code>
                    <button type="button" className={`sp-copy-btn ${copied ? 'copied' : ''}`} onClick={() => handleCopy(getServerAddress(server) || '')} title="Copiar servidor" aria-label="Copiar servidor">
                      <Clipboard size={14} />
                    </button>
                  </div>
                </div>
                <div className="sp-ftp-field">
                  <span className="sp-ftp-field-label">Puerto</span>
                  <div className="sp-ftp-field-value">
                    <code>21</code>
                    <button type="button" className="sp-copy-btn" onClick={() => handleCopy('21')} title="Copiar puerto" aria-label="Copiar puerto">
                      <Clipboard size={14} />
                    </button>
                  </div>
                </div>
                <div className="sp-ftp-field">
                  <span className="sp-ftp-field-label">Usuario</span>
                  <div className="sp-ftp-field-value">
                    <code>luxo</code>
                    <button type="button" className="sp-copy-btn" onClick={() => handleCopy('luxo')} title="Copiar usuario" aria-label="Copiar usuario">
                      <Clipboard size={14} />
                    </button>
                  </div>
                </div>
                <div className="sp-ftp-field">
                  <span className="sp-ftp-field-label">Contraseña</span>
                  <div className="sp-ftp-field-value">
                    <code>
                      {ftpPasswordVisible
                        ? server.ftp_password
                        : '•'.repeat(Math.min(server.ftp_password.length, 16))}
                    </code>
                    <button
                      type="button"
                      className="sp-copy-btn"
                      onClick={() => setFtpPasswordVisible(v => !v)}
                      title={ftpPasswordVisible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                      aria-label={ftpPasswordVisible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                      aria-pressed={ftpPasswordVisible}
                    >
                      {ftpPasswordVisible ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button type="button" className={`sp-copy-btn ${ftpCopied ? 'copied' : ''}`} onClick={() => handleCopyFtp(server.ftp_password || '')} title="Copiar contraseña" aria-label="Copiar contraseña">
                      <Clipboard size={14} />
                    </button>
                  </div>
                </div>
              </div>
              <p className="sp-ftp-hint">
                <Zap size={14} /> El servidor debe estar <strong>encendido</strong> para acceder por FTP
              </p>
            </div>
          </section>
        )}
      </div>

      {/* ============================================================
          MODALES (sub-componentes limpios en components/server/ServerModals.tsx)
          ============================================================ */}

      
      

      <ToastNotification
        message={toastMessage}
        isVisible={showToast}
        onHide={() => setShowToast(false)}
      />

      <ToastNotification
        message={errorToastMessage}
        isVisible={showErrorToast}
        onHide={() => setShowErrorToast(false)}
        type="error"
      />

      {/* ============================================================
          MODALES
          ============================================================ */}

      {showPlanModal && (
        <PlanModal
          plans={plans}
          loading={loadingPlans}
          currentPlanId={serverPlanInfo?.plan?.id}
          onSelect={handlePlanSelect}
          onClose={() => setShowPlanModal(false)}
        />
      )}

      {showModal === 'properties' && (
        <PropertiesModal
          configData={configData}
          selectedConfigFile={selectedConfigFile}
          editedContent={editedContent}
          saving={savingConfig}
          loading={loadingConfig}
          mobileTab={mobileFilesTab}
          isMobile={isMobile}
          isContentModified={!!isContentModified}
          onSelectFile={(file) => {
            setSelectedConfigFile(file);
            if (window.innerWidth <= 768) setMobileFilesTab('editor');
            if (file.content) setEditedContent(file.content);
            else { setEditedContent('Cargando...'); loadFileContent(file.file); }
          }}
          onChangeContent={setEditedContent}
          onSave={saveConfigFile}
          onChangeMobileTab={setMobileFilesTab}
          onClose={() => setShowModal(null)}
          getFileIcon={getFileIcon}
        />
      )}

      {(showModal === 'plugins' || showModal === 'mods') && (
        <AddonsModal
          kind={(showModal === 'plugins' ? 'plugin' : 'mod') as AddonKind}
          installed={showModal === 'plugins' ? plugins : mods}
          searchResults={showModal === 'plugins' ? searchResults : searchModResults}
          popularItems={showModal === 'plugins' ? popularPlugins : popularMods}
          loadingInstalled={showModal === 'plugins' ? loadingPlugins : loadingMods}
          searching={showModal === 'plugins' ? searchingPlugins : searchingMods}
          loadingPopular={showModal === 'plugins' ? loadingPopularPlugins : loadingPopularMods}
          hasMore={showModal === 'plugins' ? hasMorePlugins : hasMoreMods}
          loadingMore={showModal === 'plugins' ? loadingMorePlugins : loadingMoreMods}
          searchQuery={showModal === 'plugins' ? searchQuery : searchModQuery}
          installingId={showModal === 'plugins' ? downloadingPlugin : downloadingMod}
          uninstallingName={showModal === 'plugins' ? deletingPlugin : deletingMod}
          onSearchQueryChange={showModal === 'plugins' ? setSearchQuery : setSearchModQuery}
          onSearch={() => (showModal === 'plugins' ? searchPlugins() : searchMods())}
          onLoadMore={() => (showModal === 'plugins' ? searchPlugins(true) : searchMods(true))}
          onInstall={(id) => (showModal === 'plugins' ? downloadPlugin(id) : downloadMod(id))}
          onUninstall={(name) => (showModal === 'plugins' ? deletePlugin(name) : deleteMod(name))}
          onClose={() => setShowModal(null)}
          serverType={server.type}
          serverVersion={server.version}
        />
      )}

      {showModal === 'worlds' && (
        <WorldsModal
          mapFile={mapFile}
          uploadingMap={uploadingMap}
          downloadMapLoading={downloadingMap}
          deleteWorldLoading={deletingWorld}
          hasPendingMap={hasPendingMap}
          uploadProgress={uploadProgress}
          serverActive={!!server?.active}
          game={server?.game}
          onSelectFile={setMapFile}
          onUpload={uploadMap}
          onDownload={downloadMap}
          onDeleteWorld={deleteWorld}
          onClose={() => setShowModal(null)}
        />
      )}

      {showModal === 'whitelist' && (
        <PlayerListModal
          kind="whitelist"
          players={whitelist}
          whitelistEnabled={whitelistEnabled}
          whitelistToggling={whitelistToggling}
          loading={loadingWhitelist}
          adding={addingToWhitelist}
          removing={removingFromWhitelist}
          serverActive={!!server?.active}
          onAdd={addToWhitelist}
          onRemove={removeFromWhitelist}
          onToggleWhitelist={toggleWhitelist}
          onClose={() => setShowModal(null)}
        />
      )}

      {showModal === 'blacklist' && (
        <PlayerListModal
          kind="blacklist"
          players={blacklist}
          loading={loadingBlacklist}
          adding={addingToBlacklist}
          removing={removingFromBlacklist}
          serverActive={!!server?.active}
          onAdd={addToBlacklist}
          onRemove={removeFromBlacklist}
          onClose={() => setShowModal(null)}
        />
      )}

      {showModal === 'changeVersion' && (
        <ChangeVersionModal
          serverType={server.type}
          currentVersion={server.version}
          selectedVersion={selectedVersion}
          searchQuery={versionSearchQuery}
          changing={changingVersion}
          versions={(state.versions?.['Minecraft']?.[server.type] || []) as Array<{ id: string; name: string; popular?: boolean }>}
          serverActive={!!server?.active}
          onSelectVersion={setSelectedVersion}
          onSearchQueryChange={setVersionSearchQuery}
          onConfirm={handleChangeVersion}
          onClose={closeVersionModal}
        />
      )}

      {showModal === 'changeType' && (
        <ChangeTypeModal
          currentType={server.type}
          selectedType={selectedType}
          changing={changingType}
          serverActive={!!server?.active}
          onSelectType={setSelectedType}
          onConfirm={handleChangeType}
          onClose={() => setShowModal(null)}
        />
      )}

      {showModal === 'stats' && (
        <StatsModal
          serverId={id!}
          isActive={showModal === 'stats'}
          onClose={() => setShowModal(null)}
        />
      )}

      {showSessionInfo && (
        <SessionInfoModal
          seconds={sessionSeconds}
          cost={sessionCost}
          formatTime={formatTime}
          onClose={() => setShowSessionInfo(false)}
        />
      )}

      {showMapModal && (
        <MapSelectionModal
          maps={currentMaps}
          currentMap={currentMap}
          changing={changingMap}
          serverActive={!!server?.active}
          onSelect={handleChangeMap}
          onClose={() => setShowMapModal(false)}
        />
      )}

      {showPermissionsModal && (
        <PermissionsModal
          permissionsList={permissionsList}
          loading={loadingPermissions}
          adding={addingPermission}
          discordId={discordIdInput}
          error={permissionError}
          onDiscordIdChange={setDiscordIdInput}
          onAdd={handleAddPermission}
          onRemove={handleRemovePermission}
          onClose={() => setShowPermissionsModal(false)}
        />
      )}

      {shutdownModalOpen && (
        <ShutdownProgressModal game={server.game} />
      )}

      {startingModalOpen && (
        <StartingProgressModal game={server.game} />
      )}

      {showSubdomainPrompt && (() => {
        // El mismo modal sirve para dos modos:
        //  - "asignar": subdomain == null (auto-open al entrar al server)
        //  - "editar": subdomain ya existe (abierto desde el botón Cambiar)
        // El modo determina título, descripción, copy del CTA y mensaje
        // del toast. El preview, el input y la validación son idénticos
        // en ambos casos — la única diferencia es el frame.
        const isEditing = server?.subdomain != null;
        // Estado derivado único — evita repetir la cadena de condicionales
        // en cada lugar (preview, input wrap, status). Sirve tanto al CSS
        // (selector [data-state]) como a la lógica del JSX.
        const previewState = subdomainChecking
          ? 'checking'
          : subdomainError
          ? 'err'
          : subdomainCheck?.valid && subdomainCheck?.available && subdomainInput.trim()
          ? 'ok'
          : 'idle';
        return (
          <div
            className="sp-modal-overlay sp-modal-overlay--blocking"
            aria-hidden="false"
          >
            <div
              className="sp-modal sp-modal--sm sp-modal--subdomain"
              role="dialog"
              aria-modal="true"
              aria-labelledby="subdomain-modal-title"
              aria-describedby="subdomain-modal-desc"
            >
              <div className="sp-modal-header">
                <div className="sp-modal-header-left">
                  <div className="sp-modal-header-icon" aria-hidden="true">
                    <Earth size={16} />
                  </div>
                  <div className="sp-modal-header-text">
                    <h2 id="subdomain-modal-title" className="sp-modal-title">
                      {isEditing ? 'Cambiá tu subdominio' : 'Asigná tu subdominio'}
                    </h2>
                    <p className="sp-modal-subtitle">La dirección pública de tu servidor</p>
                  </div>
                </div>
              </div>

              <div className="sp-modal-body">
                <p id="subdomain-modal-desc" className="sp-modal-desc">
                  {isEditing
                    ? 'Cambialo cuando quieras. Los jugadores van a usar el nuevo para entrar.'
                    : 'Elegí el subdominio con el que van a entrar.'}
                </p>

                <div className="sp-subdomain-field">
                  {/* Boarding-pass: el dominio se escribe y se previsualiza
                      en el mismo elemento. La parte tipeable es el input,
                      ".node.voltary.org" es el sufijo fijo, y el mark a la
                      derecha refleja el estado de validación. */}
                  <div
                    className="sp-subdomain-preview"
                    data-state={previewState}
                    onClick={focusSubdomainInput}
                    role="presentation"
                  >
                    <input
                      id="subdomain-input"
                      ref={subdomainInputRef}
                      type="text"
                      className="sp-subdomain-input"
                      placeholder="tu-servidor"
                      value={subdomainInput}
                      onChange={(e) => setSubdomainInput(e.target.value)}
                      autoFocus
                      autoComplete="off"
                      spellCheck={false}
                      maxLength={17}
                      aria-describedby="subdomain-status"
                    />
                    <span className="sp-subdomain-preview-suffix">.node.voltary.org</span>
                    <span className="sp-subdomain-preview-mark" aria-hidden="true">
                      {previewState === 'idle' && <span className="sp-subdomain-preview-mark-dot" />}
                      {previewState === 'checking' && <Loader2 size={14} className="sp-spin" />}
                      {previewState === 'ok' && <Check size={14} />}
                      {previewState === 'err' && <CircleX size={14} />}
                    </span>
                  </div>

                  {/* Estado de validación en vivo */}
                  <div
                    className="sp-subdomain-status"
                    id="subdomain-status"
                    role="status"
                    aria-live="polite"
                  >
                    {subdomainChecking && (
                      <span className="sp-subdomain-status--checking">
                        <Loader2 size={14} className="sp-spin" /> Verificando disponibilidad…
                      </span>
                    )}
                    {!subdomainChecking && subdomainCheck?.valid && subdomainCheck?.available && subdomainInput.trim() && (
                      <span className="sp-subdomain-status--ok">
                        <Check size={14} /> Disponible
                      </span>
                    )}
                    {!subdomainChecking && subdomainError && (
                      <span className="sp-subdomain-status--err">
                        <CircleX size={14} /> {subdomainError}
                      </span>
                    )}
                    {!subdomainChecking && !subdomainError && !subdomainInput.trim() && (
                      <span className="sp-subdomain-hint">
                        3–17 caracteres · minúsculas, dígitos y guiones · sin empezar ni terminar en guion
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="sp-modal-footer">
                {isEditing && (
                  <button
                    type="button"
                    className="btn btn-ghost sp-subdomain-cancel"
                    onClick={handleCancelSubdomain}
                    disabled={savingSubdomain}
                  >
                    Cancelar
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-primary sp-subdomain-submit"
                  onClick={handleSaveSubdomain}
                  disabled={
                    savingSubdomain ||
                    !subdomainInput.trim() ||
                    subdomainChecking ||
                    (subdomainCheck !== null && (!subdomainCheck.valid || !subdomainCheck.available))
                  }
                >
                  {savingSubdomain ? (
                    <><Loader2 size={14} className="sp-spin" /> {isEditing ? 'Guardando…' : 'Asignando…'}</>
                  ) : (
                    isEditing ? 'Guardar cambios' : 'Asignar subdominio'
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showDeleteConfirm && (
        <DeleteConfirmModal
          serverName={server.name}
          deleting={deleting}
          onConfirm={handleDelete}
          onClose={() => setShowDeleteConfirm(false)}
        />
      )}

      {showImportantAlert && (
        <ImportantAlertModal
          message={importantAlertMessage}
          onClose={() => setShowImportantAlert(false)}
        />
      )}

      <ToastNotification
        message={toastMessage}
        isVisible={showToast}
        onHide={() => setShowToast(false)}
      />

      <ToastNotification
        message={errorToastMessage}
        isVisible={showErrorToast}
        onHide={() => setShowErrorToast(false)}
        type="error"
      />
      </main>

      {/*
        Mismo overlay que en el dashboard: bloquea toda interacción con el
        panel mientras la plataforma esté en mantenimiento. El handler de
        /power, /restart y /start-container también rechazan en cliente,
        pero acá dejamos al usuario sin poder clickear nada por las dudas.
      */}
      {state.maintenance && (
        <MaintenanceOverlay
          reason={state.maintenance.reason}
          end={state.maintenance.end}
        />
      )}
    </div>
  );
}
